// docs/51 friendly villages: the village seen from the side, like the intro's painted backdrops
// (ui/screens/intro/Scene.tsx). The street is a canvas at the atlas's 24 px scaled by a whole number
// (2, or 3 on a big window): far hills, a wood behind the roofs, the buildings painted pixel by pixel
// (timber, plaster, stone, logs or adobe by the country; tiled, slate, thatch or flat roofs; lit
// windows after dark, chimneys, hanging signs) with the Oryx world atlas's doors, barrels and trees,
// the lane and a verge of hedges in front. The sky, the weather and the life of the place (smoke,
// birds, a bat, fireflies, lantern light) are CSS layers over and under it. Villagers and the band
// are DOM sprites (buttons for the villagers) so they bob, stroll and take clicks; each building's
// facade is its door. Inside, one room per building is painted the same way (`roomLayout`).
// docs/64: the stage pans and zooms with the World chart's camera; the band walks in single file to
// whoever it talks to (`walk.ts`, moved by `motion.ts`); villagers amble about the lane and
// passers-by, dogs, cats and hens come and go; the inn serves a band its meal at the long table.
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { EmoteAnchor, PetWiggle } from '../../emote/EmoteBubble';
import { HeroFigure, useReducedMotion } from '../../components';
import { CLASS_FRAMES } from '../../../core/sim/adapt';
import { offBeat } from '../../../core/sim/companion';
import { usePlace, type Place } from '../../../core/audio/places';
import { playSfx } from '../../../core/audio';
import { frame as worldFrame, PROPS } from '../../../game/tiling/oryx';
import { useOverworldCamera } from '../map/useOverworldCamera';
import { useMotion, type Place as Spot } from './motion';
import { besideSpot, clearSpot, fileAlong, findPath, lengthOf, nearestFree, pointAt, roomGrid, streetGrid, FILE_GAP, type Pt, type WalkGrid } from './walk';
import { asideSpots, seatPlan, tableSeats, SEAT_GAP, type Seat } from './seats';
import { ambleOf, ambleStep, codeOf, figureAt, figureKey, heldOf, actOf, OVERLAP, overlaps, replay, SEAT, seatsOf, toFigure } from './sync';
import type { DishKind } from './critters';
import { mealAt, passerAlong, passerOf, passerRounds, serveAt, tableTalkAt, wanderOf, wanderStep, MEAL_SHOWN_S, PECK, SERVE_PACE, type Passer, type StreetShape } from './scenery';
import type { SfxKey } from '../../../core/audio/keys.gen';
import { BASE, CELL, KEEPER_WORDS, ROOM_COLS, ROOM_FLOOR_ROWS, ROOM_ROWS, STREET_H, VERGE, hashN, isInside, roomLayout, stageScale, streetCols, villageLayout, OUTSKIRTS, type Building, type Lamp, type Prop, type Room, type Station, type VillageLayout } from './layout';
import { InnLayer } from './inn/InnLayer';
import { InnOverlays } from './inn/InnOverlays';
import { HeroBits } from './inn/HeroBits';
import { bookFor } from './inn/books';
import { useInn } from './inn/store';
import { POSE, type InnData, type PoseCode } from './inn/types';
import type { InnStage } from './inn/stage';
import { STYLE_OF, type BuildingKind, type HeldSeat, type MealStage, type RemoteFigures, type VillageHero, type VillageLook, type VillageMeal, type VillageNpc, type VillageSpot, type VillageStand, type VillageSummon } from './types';
import type { VillageFigure } from '../../../bindings/types';
import { smoothServerNow } from '../../../core/time';
import { DYES } from '../../../game/itemDyes.gen';
import '../intro/intro.css';
import './village.css';

import { loadAll, itemName, Sprite, ItemIcon, Dish, Critter, type Atlas, type Sheets } from './sprites';

// ---- the painter --------------------------------------------------------------------------------
type Ctx = CanvasRenderingContext2D;
/** `mech.door`'s wood band (sfx.banks.json): a village's doors are timber. */
const DOOR_WOOD = 0;
const INK = '#1b1514';
function rect(c: Ctx, x: number, y: number, w: number, h: number, col: string) { c.fillStyle = col; c.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h)); }
function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(f >= 0 ? v + (255 - v) * f : v * (1 + f))));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => ch(v).toString(16).padStart(2, '0')).join('')}`;
}
function drawWorld(c: Ctx, a: Atlas, n: number, x: number, y: number, flip = false) {
  const f = a.frames[worldFrame(n)];
  if (!f) return;
  const px = Math.round(x), py = Math.round(y + CELL - f.h);
  if (!flip) { c.drawImage(a.img, f.x, f.y, f.w, f.h, px, py, f.w, f.h); return; }
  c.save(); c.translate(px + f.w, py); c.scale(-1, 1); c.drawImage(a.img, f.x, f.y, f.w, f.h, 0, 0, f.w, f.h); c.restore();
}
function drawItem(c: Ctx, a: Atlas | undefined, n: number, x: number, y: number) {
  const f = a?.frames[itemName(n)];
  if (a && f) c.drawImage(a.img, f.x, f.y, f.w, f.h, Math.round(x), Math.round(y), f.w, f.h);
}
const drawProp = (c: Ctx, a: Atlas, p: Prop, phase: number) => drawWorld(c, a, p.anim && phase ? p.anim[phase % p.anim.length]! : p.frame, p.x * CELL + (p.dx ?? 0), p.y * CELL + (p.dy ?? 0), p.flip);

const PLASTER = ['#e6d8b2', '#dcc89c', '#ebe3cd', '#e3c9b2', '#d9d0b0'];
const TIMBER = '#5a3922', TIMBER_HI = '#7b5233', TIMBER_SH = '#3c2515';
const STONES = { stone: { base: '#8d8a84', hi: '#aba8a0', sh: '#67645f', mortar: '#4b4844' }, tan: { base: '#cdb67b', hi: '#e3d29b', sh: '#a68f58', mortar: '#7e6a3e' } };
const LOG = { base: '#7b5234', hi: '#9d6c45', sh: '#4f331f', end: '#c0925e' };
const ROOFS: Record<string, [string, string, string]> = { tile: ['#a9533b', '#cc7151', '#73321f'], slate: ['#525c70', '#717e96', '#363c4c'], thatch: ['#c39a4f', '#e0bd6d', '#86642c'], clay: ['#c27a4e', '#dc9a68', '#8a4d2c'] };
const SHUTTERS = ['#3f6a4a', '#3a5a88', '#8c3c33', '#6b5a3a'];

/** Geometry shared by the painter and the DOM layers (all in atlas pixels on the street canvas). */
export function facadeBox(b: Building) {
  const L = b.x * CELL, R = (b.x + b.w) * CELL, W = R - L;
  const bottom = (BASE + 1) * CELL;
  const wallTop = b.style === 'temple' ? BASE * CELL - CELL - 12 : b.floors === 2 ? BASE * CELL - CELL - 4 : BASE * CELL - 7;
  const roofH = b.roof === 'flat' ? 5 : b.roof === 'gable' ? Math.round(W * 0.4) : 16 + b.w * 2;
  const top = wallTop - roofH;
  const tower = b.style === 'temple' ? { x: b.door * CELL + 4, w: 16, top: b.roof === 'flat' ? top - 2 : 18, spire: b.roof === 'flat' ? 0 : 2 } : undefined;
  return { L, R, W, bottom, wallTop, roofH, top: tower ? Math.min(top, tower.spire) : top, roofTop: top, tower };
}
/** The chimney's top (atlas pixels), where its smoke rises from. */
export function chimneyTop(b: Building): { x: number; y: number } | undefined {
  if (b.chimney === undefined) return undefined;
  const f = facadeBox(b);
  const cx = b.chimney === b.x ? f.L + 7 : f.R - 9;
  return { x: cx + 3, y: f.roofTop - (b.style === 'smithy' ? 11 : 8) };
}

function paintWall(c: Ctx, b: Building, x0: number, y0: number, x1: number, y1: number, seed: number) {
  const plaster = PLASTER[Math.floor(b.tone * PLASTER.length)]!;
  if (b.wall === 'stone' || b.wall === 'tan') {
    const p = STONES[b.wall];
    rect(c, x0, y0, x1 - x0, y1 - y0, p.mortar);
    const rowH = b.wall === 'tan' ? 7 : 5;
    for (let y = y1 - rowH, row = 0; y > y0 - rowH; y -= rowH, row++) {
      let x = x0 - (row % 2 ? 4 : 0);
      for (let k = 0; x < x1; k++) {
        const bw = (b.wall === 'tan' ? 10 : 7) + Math.floor(hashN(seed, row, k, x0) * 5);
        const v = hashN(seed, row, k, 3);
        const col = v < 0.2 ? p.sh : v > 0.85 ? p.hi : p.base;
        const bx = Math.max(x0, x), by = Math.max(y0, y), ex = Math.min(x1, x + bw - 1), ey = Math.min(y1, y + rowH - 1);
        if (ex > bx && ey > by) { rect(c, bx, by, ex - bx, ey - by, col); rect(c, bx, by, ex - bx, 1, shade(col, 0.18)); }
        x += bw;
      }
    }
    return;
  }
  if (b.wall === 'log') {
    for (let y = y0, k = 0; y < y1; y += 4, k++) {
      rect(c, x0, y, x1 - x0, 4, LOG.base);
      rect(c, x0, y, x1 - x0, 1, LOG.hi);
      rect(c, x0, y + 3, x1 - x0, 1, LOG.sh);
      for (let x = x0 + 3 + (k % 3) * 5; x < x1 - 2; x += 13) rect(c, x, y + 1, 3, 1, LOG.sh);
    }
    // the log ends at the corners
    for (let y = y0; y < y1; y += 4) { rect(c, x0 - 1, y, 3, 3, LOG.end); rect(c, x1 - 2, y, 3, 3, LOG.end); }
    return;
  }
  rect(c, x0, y0, x1 - x0, y1 - y0, plaster);
  // a little grain in the plaster
  for (let i = 0; i < (x1 - x0) * (y1 - y0) / 30; i++) {
    const px = x0 + Math.floor(hashN(seed, i, 1) * (x1 - x0)), py = y0 + Math.floor(hashN(seed, i, 2) * (y1 - y0));
    rect(c, px, py, 1, 1, shade(plaster, hashN(seed, i, 3) < 0.5 ? -0.07 : 0.06));
  }
  if (b.wall === 'plaster') {
    // stone quoins at the corners
    for (let y = y1 - 4, k = 0; y > y0; y -= 4, k++) {
      const qw = k % 2 ? 4 : 6;
      rect(c, x0, y, qw, 3, '#9a958a'); rect(c, x1 - qw, y, qw, 3, '#9a958a');
    }
  }
}

function paintWindow(c: Ctx, x: number, y: number, w: number, h: number, shutter: string | undefined, sill: boolean, snowy: boolean) {
  if (shutter) { rect(c, x - 4, y - 1, 4, h + 2, INK); rect(c, x - 3, y, 2, h, shutter); rect(c, x + w, y - 1, 4, h + 2, INK); rect(c, x + w + 1, y, 2, h, shutter); }
  rect(c, x - 1, y - 1, w + 2, h + 2, INK);
  rect(c, x, y, w, h, '#2f3a52');
  rect(c, x, y, w, 1, '#55658a');
  rect(c, x + 1, y + 1, 2, 1, '#8ea2c4'); rect(c, x + 1, y + 2, 1, 1, '#8ea2c4');
  rect(c, x + Math.floor(w / 2), y, 1, h, TIMBER_SH); rect(c, x, y + Math.floor(h / 2), w, 1, TIMBER_SH);
  if (sill) { rect(c, x - 2, y + h + 1, w + 4, 2, TIMBER_HI); rect(c, x - 2, y + h + 3, w + 4, 1, INK); if (snowy) rect(c, x - 2, y + h, w + 4, 1, '#f2f6ff'); }
}
/** A window's glass, lit from inside (drawn after the night's tint, so it glows). */
function paintLitGlass(c: Ctx, x: number, y: number, w: number, h: number, v: number) {
  rect(c, x, y, w, h, v < 0.3 ? '#f0a848' : '#ffc95e');
  rect(c, x + 1, y + 1, w - 2, h - 2, v < 0.3 ? '#f8bf62' : '#ffe08c');
  rect(c, x + Math.floor(w / 2), y, 1, h, '#7a4a1e'); rect(c, x, y + Math.floor(h / 2), w, 1, '#7a4a1e');
}

/** Leaded glass in diamonds of colour: dim by day from outside, glowing when lit from within. */
function stainedGlass(c: Ctx, x: number, y: number, w: number, h: number, lit: boolean) {
  const day = ['#2c3a6a', '#3d2a5a', '#2a4a5a', '#5a3a2a'], glow = ['#ffcf6a', '#e86a4a', '#6a8cf0', '#f8e09a'];
  for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) {
    const d = (xx + yy) % 4 === 0 || (xx - yy + 64) % 4 === 0;
    rect(c, x + xx, y + yy, 1, 1, d ? (lit ? '#7a4a1e' : '#151a2c') : (lit ? glow : day)[(Math.floor((xx + yy) / 4) + Math.floor((xx - yy + 64) / 4)) % 4]!);
  }
}

function paintRoofTiles(c: Ctx, b: Building, rows: Array<[number, number, number]>, seed: number, snowy: boolean) {
  // rows: [y, xLeft, xRight]; courses of 4 px with staggered seams
  const [base, hi, sh] = ROOFS[b.roofTone] ?? ROOFS.tile!;
  const tone = (b.tone - 0.5) * 0.14;
  const B = shade(base, tone), H = shade(hi, tone), S = shade(sh, tone);
  const n = rows.length;
  rows.forEach(([y, xl, xr], i) => {
    const fromTop = n - 1 - i;           // i counts from the eave up
    const course = Math.floor(fromTop / 4), j = fromTop % 4;
    if (b.roofTone === 'thatch') {
      rect(c, xl, y, xr - xl, 1, j === 3 ? S : B);
      for (let x = xl; x < xr; x++) if (hashN(seed, x, y) < 0.22) rect(c, x, y, 1, 1, hashN(seed, x, y, 1) < 0.5 ? H : S);
    } else {
      rect(c, xl, y, xr - xl, 1, j === 3 ? S : j === 0 ? H : B);
      if (j === 1 || j === 2) for (let x = xl + ((course % 2) * 3) + 1; x < xr; x += 6) rect(c, x, y, 1, 1, S);
    }
    rect(c, xl, y, 1, 1, INK); rect(c, xr - 1, y, 1, 1, INK);
  });
  const [ty, txl, txr] = rows[0]!;
  rect(c, txl, ty, txr - txl, 1, INK);
  if (snowy) {
    for (let k = 0; k < Math.min(4, n); k++) { const [y, xl, xr] = rows[k]!; rect(c, xl + 1, y + (k === 0 ? 0 : 0), xr - xl - 2, 1, k === 3 ? '#d6e0f0' : '#f4f7ff'); }
    // drips and drifts along the eave
    const [ey, exl, exr] = rows[n - 1]!;
    rect(c, exl, ey - 1, exr - exl, 2, '#eef3ff');
    for (let x = exl + 2; x < exr - 2; x += 3 + Math.floor(hashN(seed, x) * 4)) rect(c, x, ey + 1, 1, 1 + Math.floor(hashN(seed, x, 2) * 3), '#dfe8f8');
  }
}

function paintBuilding(c: Ctx, b: Building, a: Sheets, seed: number, snowy: boolean, night: boolean) {
  const f = facadeBox(b);
  const { L, R, W, bottom, wallTop } = f;
  const s = seed + b.x * 31;
  // ── the body ──
  rect(c, L, wallTop - 1, W, bottom - wallTop + 1, INK);
  paintWall(c, b, L + 1, wallTop, R - 1, bottom, s);
  // a gable end above the wall: the triangle continues the wall up to the ridge
  const apexX = L + W / 2;
  if (b.roof === 'gable') {
    c.save();
    c.beginPath(); c.moveTo(L + 1, wallTop + 1); c.lineTo(apexX, f.roofTop + 3); c.lineTo(R - 1, wallTop + 1); c.closePath(); c.clip();
    paintWall(c, b, L + 1, f.roofTop, R - 1, wallTop + 1, s + 7);
    if (b.wall === 'timber' || b.wall === 'plaster') {
      rect(c, apexX - 1, f.roofTop, 2, wallTop - f.roofTop, TIMBER);                         // king post
      rect(c, L, wallTop - Math.round(f.roofH * 0.45), W, 2, TIMBER);                       // collar beam
    }
    c.restore();
    // an attic window under the ridge
    if (b.style !== 'temple' && f.roofH > 20) paintWindow(c, apexX - 3, wallTop - Math.round(f.roofH * 0.45) - 9, 6, 6, undefined, false, false);
  }
  // timber framing: posts at the corners and between bays, the floor beam, the sill
  if (b.wall === 'timber') {
    const beam = (x: number, y: number, w: number, h: number) => { rect(c, x, y, w, h, TIMBER); rect(c, x, y, w, 1, TIMBER_HI); };
    beam(L + 1, wallTop, W - 2, 2);
    if (b.floors === 2) beam(L + 1, BASE * CELL - 2, W - 2, 3);
    for (let k = 0; k <= b.w; k++) { const x = Math.min(R - 3, Math.max(L + 1, L + k * CELL - 1)); rect(c, x, wallTop, 2, bottom - wallTop, TIMBER); rect(c, x, wallTop, 1, bottom - wallTop, TIMBER_HI); }
    if (b.floors === 2) for (let k = 0; k < b.w; k++) {
      // braces in the upper bays, either side of the window
      const x = L + k * CELL;
      for (let i = 0; i < 8; i++) { rect(c, x + 1 + i * 0.6, BASE * CELL - 3 - i * 2.4, 2, 2, TIMBER_SH); rect(c, x + CELL - 3 - i * 0.6, BASE * CELL - 3 - i * 2.4, 2, 2, TIMBER_SH); }
    }
  }
  // a stone plinth along the street
  if (b.wall !== 'stone' && b.wall !== 'tan') { rect(c, L + 1, bottom - 4, W - 2, 4, '#77746c'); rect(c, L + 1, bottom - 4, W - 2, 1, '#9a978e'); for (let x = L + 4; x < R - 2; x += 7) rect(c, x, bottom - 3, 1, 3, '#55524c'); }

  // ── windows ──
  const shutter = b.kind === 'house' && b.wall !== 'stone' && b.roofTone !== 'clay' ? SHUTTERS[Math.floor(b.tone * 7) % SHUTTERS.length] : undefined;
  if (b.style === 'temple') {
    // tall lancets of coloured glass either side of the door, a rose window over it
    for (let k = 0; k < b.w; k++) {
      const cx = L + k * CELL + 12;
      if (b.x + k === b.door) { rect(c, cx - 6, wallTop + 4, 12, 12, INK); c.fillStyle = '#2b3a66'; c.beginPath(); c.arc(cx, wallTop + 10, 5, 0, Math.PI * 2); c.fill(); continue; }
      rect(c, cx - 4, wallTop + 7, 8, bottom - wallTop - 15, INK);
      rect(c, cx - 3, wallTop + 6, 6, 1, INK); rect(c, cx - 2, wallTop + 5, 4, 1, INK);
      stainedGlass(c, cx - 3, wallTop + 7, 6, bottom - wallTop - 17, false);
    }
  } else {
    for (const wx of b.windows) paintWindow(c, wx * CELL + 7, BASE * CELL + 3, 10, 9, shutter, true, snowy);
    for (const wx of b.upper) paintWindow(c, wx * CELL + 7, wallTop + 6, 10, 10, shutter, true, snowy);
  }
  // a shop's striped awning over its window (docs/142: the dyer's in its dyes, with a valance of dyed lengths)
  if (b.style === 'shop') {
    const ax = L + 2, aw = W - 4, ay = BASE * CELL - 2;
    rect(c, ax - 1, ay - 1, aw + 2, 8, INK);
    const dyer = b.kind === 'dyer';
    for (let x = 0; x < aw; x++) {
      const k = Math.floor(x / 4);
      const col = dyer ? `#${DYES[(k + b.x) % DYES.length]!.ramp[2]}` : k % 2 === 0 ? '#b8443a' : '#efe4c8';
      rect(c, ax + x, ay, 1, 6 - (x % 4 === 3 ? 1 : 0), col);
      // the dyer's lengths of cloth hang down from it, every other stripe, longer and shorter
      if (dyer && k % 2 === 0 && x % 4 !== 3 && !(b.windows.length === 0)) {
        const cx = ax + x, door = (b.door - b.x) * CELL + L;
        if (cx >= door - 1 && cx <= door + CELL) continue;
        const h = 4 + ((k * 5) % 3) * 2;
        rect(c, cx, ay + 6, 1, h, x % 4 === 0 ? `#${DYES[(k + b.x) % DYES.length]!.ramp[1]}` : col);
        rect(c, cx, ay + 6 + h, 1, 1, INK);
      }
    }
    rect(c, ax, ay, aw, 1, '#ffffff33');
    if (snowy) rect(c, ax, ay - 1, aw, 2, '#f2f6ff');
  }

  // ── the roof ──
  const rows: Array<[number, number, number]> = [];
  if (b.roof === 'eaves') {
    for (let y = f.roofTop; y < wallTop; y++) { const i = wallTop - 1 - y; const inset = Math.round(i * 0.55); rows.push([y, L - 3 + inset, R + 3 - inset]); }
    paintRoofTiles(c, b, rows, s, snowy);
    rect(c, L + 1, wallTop, W - 2, 2, '#00000040');
  } else if (b.roof === 'gable') {
    // two thick bands along the slopes, overhanging the walls
    const [base, hi, sh] = ROOFS[b.roofTone] ?? ROOFS.tile!;
    const band = 6;
    for (let x = L - 3; x <= R + 3; x++) {
      const t = 1 - Math.abs(x - apexX) / (W / 2 + 3);
      const y = Math.round(wallTop + 2 - t * (f.roofH + 1));
      rect(c, x, y - 1, 1, band + 2, INK);
      rect(c, x, y, 1, band, base);
      rect(c, x, y, 1, 1, snowy ? '#f4f7ff' : hi);
      if (snowy) rect(c, x, y + 1, 1, 1, '#e6edf9');
      rect(c, x, y + band - 1, 1, 1, sh);
      if ((x - L) % 4 === 0) rect(c, x, y + 2, 1, band - 3, sh);
    }
  } else {
    // flat: a parapet with rounded merlons, the beam ends (vigas) showing under it
    const p = STONES.tan;
    rect(c, L, f.roofTop - 1, W, wallTop - f.roofTop + 2, INK);
    rect(c, L + 1, f.roofTop, W - 2, wallTop - f.roofTop + 1, p.hi);
    for (let x = L + 3; x < R - 4; x += 8) { rect(c, x, f.roofTop - 3, 5, 3, INK); rect(c, x + 1, f.roofTop - 2, 3, 3, p.hi); }
    for (let x = L + 4; x < R - 3; x += 7) { rect(c, x, wallTop + 3, 3, 2, TIMBER_SH); rect(c, x, wallTop + 3, 1, 1, TIMBER_HI); }
  }

  // ── the temple's tower and spire (or a dome on a flat-roofed temple) ──
  if (f.tower) {
    const t = f.tower;
    const stoneB: Building = { ...b, wall: b.wall === 'tan' ? 'tan' : 'stone' };
    if (b.roof === 'flat') {
      rect(c, t.x - 4, t.top + 6, t.w + 8, wallTop - t.top - 6, INK);
      paintWall(c, stoneB, t.x - 3, t.top + 7, t.x + t.w + 3, f.roofTop, s + 3);
      c.fillStyle = INK; c.beginPath(); c.arc(t.x + t.w / 2, t.top + 8, 12, Math.PI, 0); c.fill();
      c.fillStyle = '#e9e2cc'; c.beginPath(); c.arc(t.x + t.w / 2, t.top + 8, 11, Math.PI, 0); c.fill();
      rect(c, t.x + t.w / 2 - 6, t.top + 1, 3, 3, '#ffffff');
      rect(c, t.x + t.w / 2 - 1, t.top - 8, 2, 6, '#e0b24c');
    } else {
      rect(c, t.x - 1, t.top - 1, t.w + 2, f.roofTop - t.top + 8, INK);
      paintWall(c, stoneB, t.x, t.top, t.x + t.w, f.roofTop + 8, s + 3);
      // the belfry and its bell
      rect(c, t.x + 4, t.top + 4, 8, 10, INK); rect(c, t.x + 5, t.top + 6, 6, 8, '#141018');
      rect(c, t.x + 6, t.top + 8, 4, 4, '#d8a640'); rect(c, t.x + 7, t.top + 12, 2, 1, '#d8a640');
      // the spire
      const [base, hi] = ROOFS.slate!;
      for (let y = t.spire; y < t.top; y++) {
        const half = Math.max(0, Math.round(((y - t.spire) / (t.top - t.spire)) * (t.w / 2 + 2)));
        rect(c, t.x + t.w / 2 - half - 1, y, half * 2 + 2, 1, INK);
        if (half > 0) { rect(c, t.x + t.w / 2 - half, y, half, 1, snowy && y % 5 === 0 ? '#eef3ff' : hi); rect(c, t.x + t.w / 2, y, half, 1, base); }
      }
      rect(c, t.x + t.w / 2 - 1, t.spire - 3, 2, 4, '#e0b24c');
    }
  }
  // ── chimney ──
  const ch = chimneyTop(b);
  if (ch) {
    const cx = ch.x - 3, h = b.roof === 'gable' ? f.roofH * 0.6 + 8 : 10;
    const bw = b.style === 'smithy' ? 8 : 6;
    rect(c, cx - 1, ch.y - 1, bw + 2, h + 2, INK);
    for (let y = ch.y, k = 0; y < ch.y + h; y += 3, k++) { rect(c, cx, y, bw, 3, k % 2 ? '#94473a' : '#a85444'); rect(c, cx + (k % 2 ? 3 : 1), y + 2, 1, 1, '#5e2a22'); }
    rect(c, cx - 2, ch.y - 2, bw + 4, 3, INK); rect(c, cx - 1, ch.y - 2, bw + 2, 1, snowy ? '#f4f7ff' : '#6b6660');
  }

  // ── the door ──
  const doorFrame = b.style === 'temple' ? PROPS.doors.blue : b.style === 'smithy' ? PROPS.doors.archOpen : b.style === 'inn' ? PROPS.doors.wood2 : b.style === 'shop' ? PROPS.doors.wood : [PROPS.doors.wood, PROPS.doors.plain, PROPS.doors.knocker][Math.floor(b.tone * 3)]!;
  drawWorld(c, a.world, doorFrame, b.door * CELL, BASE * CELL);
  // a worn stone step before the door
  rect(c, b.door * CELL + 1, bottom - 1, CELL - 2, 4, INK); rect(c, b.door * CELL + 2, bottom - 1, CELL - 4, 3, '#9a968c'); rect(c, b.door * CELL + 2, bottom - 1, CELL - 4, 1, snowy ? '#f4f7ff' : '#bdb9ae');
  if (b.style === 'smithy') { rect(c, b.door * CELL + 6, BASE * CELL + 8, 12, 15, night ? '#2a1208' : '#3a1a0c'); }

  // ── a hanging sign by the door: the inn's jug, the shop's purse, the smith's hammer ──
  // docs/112: each workshop hangs its trade's sign: an orb, a mushroom, a runestone, a star-glass, a
  // blade, a shield, a bow, a glove
  const icon = b.kind === 'dyer' ? -1 : SIGNS[b.kind] ?? (b.style === 'inn' ? 19 : b.style === 'shop' ? 72 : b.style === 'smithy' ? 164 : undefined);
  if (icon !== undefined) {
    const right = b.door < b.x + b.w - 1;
    const bx = right ? b.door * CELL + CELL + 1 : b.door * CELL - 19, by = b.floors === 2 && b.style !== 'shop' ? BASE * CELL - 16 : BASE * CELL - 14;
    rect(c, right ? b.door * CELL + 20 : bx + 2, by - 3, 18, 2, INK);       // the bracket
    rect(c, bx + 3, by - 1, 1, 2, INK); rect(c, bx + 14, by - 1, 1, 2, INK);
    rect(c, bx, by + 1, 18, 17, INK); rect(c, bx + 1, by + 2, 16, 15, '#8a5c34'); rect(c, bx + 1, by + 2, 16, 1, '#a97a4a');
    // docs/142: the dyer's sign is painted, not an item: a vat with cloth hanging over its rim in three dyes
    if (icon < 0) paintDyerSign(c, bx + 1, by + 2); else drawItem(c, a.items, icon, bx + 1, by + 1);
    if (snowy) rect(c, bx, by + 1, 18, 1, '#f4f7ff');
  }
}

/** docs/142: the dyer's hanging sign on its 16×15 board: a wooden vat of blue dye, red and gold cloth over its rim. */
function paintDyerSign(c: Ctx, x: number, y: number) {
  const [red, gold, blue] = (['red', 'gold', 'blue'] as const).map((k) => DYES.find((d) => d.key === k)!.ramp.map((h) => `#${h}`));
  rect(c, x + 2, y + 6, 12, 8, INK);                          // the vat
  rect(c, x + 3, y + 7, 10, 6, '#7a4e2a'); rect(c, x + 3, y + 7, 10, 1, '#a0703e');
  rect(c, x + 3, y + 9, 10, 1, '#4a3020'); rect(c, x + 3, y + 12, 10, 1, '#4a3020');   // hoops
  rect(c, x + 3, y + 6, 10, 2, blue![2]!); rect(c, x + 4, y + 6, 5, 1, blue![3]!);      // the dye
  rect(c, x + 1, y + 2, 4, 9, INK); rect(c, x + 2, y + 3, 2, 7, red![2]!); rect(c, x + 2, y + 3, 1, 7, red![3]!);   // red cloth over the left rim
  rect(c, x + 11, y + 1, 4, 10, INK); rect(c, x + 12, y + 2, 2, 8, gold![2]!); rect(c, x + 13, y + 2, 1, 8, gold![1]!); // gold over the right
  rect(c, x + 4, y + 1, 8, 2, INK); rect(c, x + 5, y + 1, 6, 1, '#d8c7a3');           // the stirring stick
}


/** The far country behind the village: a range of mountains, rolling hills with a far wood on them,
 * a few far roofs (and a spire in a town), each layer paler with distance. */
/** Rows of sky the far country rises into above the street's canvas (its own canvas, behind). */
export const FAR_HEAD = 3;
function paintFar(c: Ctx, w: number, seed: number, l: VillageLayout, night: boolean) {
  const W = w * CELL, oy = FAR_HEAD * CELL, foot = oy + BASE * CELL + CELL;
  const desert = l.theme.biome === 'desert', snowy = l.theme.snowy;
  const ash = l.theme.biome === 'ashlands';
  // docs/23 Y10: the Ashlands' far mountains are black cones whose "snow" is the lava glowing at their tops
  const pal = night && ash ? { mtn: '#2a1814', snow: '#a8361a', hill: '#1e1210', wood: '#221412', roof: '#1a0e0c' }
    : night
    ? { mtn: '#28334f', snow: '#4c5878', hill: '#1f2942', wood: '#18213a', roof: '#151c30' }
    : ash ? { mtn: '#3e3330', snow: '#c8582a', hill: '#4a3c36', wood: '#3a2c26', roof: '#2e2422' }
    : desert ? { mtn: '#d9bf98', snow: '#efe2c8', hill: '#cfa676', wood: '#b48a5c', roof: '#a87e54' }
    : snowy ? { mtn: '#a9b8cf', snow: '#f2f6fc', hill: '#bccadb', wood: '#7e93a8', roof: '#6d7f94' }
    // docs/23 Y9: under the rock, the far cave wall and its pillars in the glow
    : l.theme.biome === 'underdark' ? { mtn: '#3c4a52', snow: '#55707a', hill: '#2f3d44', wood: '#2c5552', roof: '#26343a' }
    : l.theme.biome === 'jungle' ? { mtn: '#86a9b0', snow: '#c8dde0', hill: '#6f9a84', wood: '#4f7e5e', roof: '#5d6d5c' }
    : { mtn: '#94acc6', snow: '#e8eef8', hill: '#86a89a', wood: '#678a6e', roof: '#5f7466' };
  const n = (t: number, f: number, salt: number) => 0.55 * Math.sin(t * f + (seed % 13) + salt) + 0.3 * Math.sin(t * f * 2.7 + salt * 1.7) + 0.15 * Math.sin(t * f * 6.3 + salt * 3.1);
  // mountains: seeded peaks, each lit on its left flank and shaded on its right, snow on the high ones
  const peaks = Array.from({ length: Math.max(4, Math.round(W / 70)) }, (_, i) => ({ p: ((i + 0.2 + hashN(seed, i, 61) * 0.6) / Math.max(4, Math.round(W / 70))) * W, h: 20 + hashN(seed, i, 62) * 46, hw: 44 + hashN(seed, i, 63) * 50 }));
  for (let x = 0; x < W; x++) {
    let best = 0, who = peaks[0]!;
    for (const k of peaks) { const v = k.h * (1 - Math.abs(x - k.p) / k.hw); if (v > best) { best = v; who = k; } }
    const y = Math.round(oy + 64 - best - 1.5 * Math.sin(x * 0.31 + seed));
    const lit = x < who.p;
    rect(c, x, y, 1, foot - y, lit ? shade(pal.mtn, 0.12) : pal.mtn);
    const capLine = oy + 64 - who.h * 0.6;
    if (!desert && who.h > 30 && y < capLine) rect(c, x, y, 1, Math.round(capLine - y + (hashN(seed, x >> 2) * 3)), lit ? pal.snow : shade(pal.snow, -0.14));
  }
  // rolling hills, a far wood along their crest
  const hill = (x: number) => oy + 74 - 10 * n(x / W, 9, 4.1);
  for (let x = 0; x < W; x++) { const y = Math.round(hill(x)); rect(c, x, y, 1, foot - y, pal.hill); }
  if (!desert) for (let x = 0; x < W; x += 3) {
    if (hashN(seed, x, 51) < 0.35) continue;
    const y = Math.round(hill(x)), r = 2 + Math.floor(hashN(seed, x, 52) * 3);
    for (let k = -r; k <= r; k++) { const hh = Math.round(Math.sqrt(r * r - k * k) * 1.4); rect(c, x + k, y - hh + 1, 1, hh + 2, pal.wood); }
  }
  // far roofs on the hills (a town has a spire)
  const count = l.w > 26 ? 6 : 4;
  for (let i = 0; i < count; i++) {
    const x = Math.floor(hashN(seed, i, 41) * (W - 30)) + 10, bw = 7 + Math.floor(hashN(seed, i, 42) * 7);
    const y = Math.round(hill(x)) + 2;
    rect(c, x, y, bw, 12, pal.roof);
    for (let k = 0; k < 4; k++) rect(c, x - 1 + k, y - 4 + k, bw + 2 - k * 2, 1, pal.roof);
  }
  if (l.buildings.some((b) => b.style === 'temple')) {
    const x = Math.floor(W * (0.7 + hashN(seed, 9) * 0.2)), y = Math.round(hill(x));
    rect(c, x, y - 18, 5, 22, pal.roof); for (let k = 0; k < 9; k++) rect(c, x + 2 - Math.floor(k / 3), y - 27 + k, 1 + Math.floor(k / 3) * 2, 1, pal.roof);
  }
}

/** A small lantern (atlas pixels): `lit` after dark. */
function paintLantern(c: Ctx, x: number, y: number, lit: boolean) {
  rect(c, x + 1, y, 4, 1, INK); rect(c, x, y + 1, 6, 1, INK);
  rect(c, x, y + 2, 6, 6, INK);
  rect(c, x + 1, y + 3, 4, 4, lit ? '#ffd46a' : '#4a4f5c');
  if (lit) rect(c, x + 2, y + 4, 2, 2, '#fff3c0'); else rect(c, x + 1, y + 3, 1, 1, '#8a93a8');
  rect(c, x + 1, y + 8, 4, 1, INK);
}

function hazeOver(c: Ctx, col: string, alpha: number, draw: (c: Ctx) => void, w: number, h: number) {
  const off = document.createElement('canvas');
  off.width = w; off.height = h;
  const o = off.getContext('2d')!;
  o.imageSmoothingEnabled = false;
  draw(o);
  o.globalCompositeOperation = 'source-atop';
  o.globalAlpha = alpha; o.fillStyle = col; o.fillRect(0, 0, w, h);
  c.drawImage(off, 0, 0);
}

function paintLampPost(c: Ctx, lamp: Lamp, snowy: boolean, lit: boolean) {
  const x = Math.round(lamp.x * CELL), foot = Math.round(lamp.y * CELL) + 10;
  rect(c, x - 1, foot - 30, 3, 30, INK); rect(c, x, foot - 29, 1, 28, '#4a4a52');
  rect(c, x - 3, foot - 3, 7, 3, INK); rect(c, x - 2, foot - 31, 7, 2, INK);
  paintLantern(c, x + 1, foot - 30, lit);
  if (snowy) rect(c, x - 2, foot - 32, 7, 1, '#f4f7ff');
}
/** A lantern on an iron bracket out from a wall. */
function paintWallLamp(c: Ctx, lamp: Lamp, lit: boolean) {
  const x = Math.round(lamp.x * CELL), y = Math.round(lamp.y * CELL);
  rect(c, x - 4, y - 2, 8, 1, INK); rect(c, x, y - 1, 1, 2, INK);
  paintLantern(c, x - 3, y, lit);
}
/** A wooden fence along the verge: posts and two rails, a cap of snow in winter. */
function paintFence(c: Ctx, cells: number[], y0: number, snowy: boolean) {
  const set = new Set(cells);
  for (const cx of cells) {
    const x0 = cx * CELL, top = y0 + 6;
    const L = set.has(cx - 1) ? x0 : x0 + 2, R = set.has(cx + 1) ? x0 + CELL : x0 + CELL - 2;
    for (const ry of [top + 4, top + 10]) { rect(c, L, ry - 1, R - L, 4, INK); rect(c, L, ry, R - L, 2, '#8a5c34'); rect(c, L, ry, R - L, 1, '#a97a4a'); }
    for (const px of [x0 + 2, x0 + 14]) { if (px + 3 > R + 1 && !set.has(cx + 1)) continue; rect(c, px - 1, top - 1, 5, 18, INK); rect(c, px, top, 3, 16, '#7a4f2c'); rect(c, px, top, 1, 16, '#9a6a40'); if (snowy) rect(c, px - 1, top - 2, 5, 2, '#f4f7ff'); }
    if (snowy) rect(c, L, top + 2, R - L, 1, '#eef3ff');
  }
}

/** Multiply the night (or a grey sky) into the painted pixels, keeping their alpha (the sky shows through). */
function tint(c: Ctx, col: string) {
  const { width: w, height: h } = c.canvas;
  const shape = document.createElement('canvas');
  shape.width = w; shape.height = h;
  shape.getContext('2d')!.drawImage(c.canvas, 0, 0);
  c.globalCompositeOperation = 'multiply'; c.fillStyle = col; c.fillRect(0, 0, w, h);
  c.globalCompositeOperation = 'destination-in'; c.drawImage(shape, 0, 0);
  c.globalCompositeOperation = 'source-over';
}

export interface Painted { tint?: string; far?: HTMLCanvasElement; base: HTMLCanvasElement; glow: HTMLCanvasElement; anims: Prop[]; front: Prop[]; fences?: number[]; snowy?: boolean; pews?: Room['pews'] }

/** Which windows burn after dark (most of a business's, about two in three of a house's). */
export const windowLit = (seed: number, b: Building, x: number) => hashN(seed, x, b.x, 77) < (b.kind === 'house' ? 0.62 : 0.92);

export function paintStreet(l: VillageLayout, look: VillageLook, a: Sheets): Painted {
  const W = l.w * CELL, H = l.h * CELL;
  const base = document.createElement('canvas'); base.width = W; base.height = H;
  const c = base.getContext('2d')!; c.imageSmoothingEnabled = false;
  const night = look.night;
  const far = document.createElement('canvas'); far.width = W; far.height = (FAR_HEAD + BASE + 1) * CELL;
  const fc = far.getContext('2d')!; fc.imageSmoothingEnabled = false;
  paintFar(fc, l.w, look.seed, l, night);
  // the wood behind, hazed by distance
  hazeOver(c, night ? '#1a2240' : l.theme.snowy ? '#c8d4e6' : l.theme.biome === 'desert' ? '#d8c0a0' : l.theme.biome === 'underdark' ? '#2c4048' : l.theme.biome === 'ashlands' ? '#4a3028' : '#7f9fb4', night ? 0.35 : 0.34, (o) => { for (const p of l.props) if (p.layer === 'back') drawProp(o, a.world, p, 0); }, W, H);
  for (let y = BASE; y < l.h; y++) for (let x = 0; x < l.w; x++) for (const f of l.ground[y]![x]!) drawWorld(c, a.world, f, x * CELL, y * CELL);
  // wheel ruts and pebbles in an earth lane
  {
    const rut = l.theme.lane === 'sand' ? '#c79a6a' : l.theme.snowy ? '#8a7a6a' : '#5b3d24';
    for (const ry of [6 * CELL + 9, 7 * CELL + 3]) for (let x = 0; x < W; x++) if (hashN(look.seed, x >> 3, ry) > 0.18) { c.globalAlpha = 0.45; rect(c, x, ry + (hashN(look.seed, x >> 4, ry, 1) < 0.5 ? 0 : 1), 1, 2, rut); c.globalAlpha = 1; }
    for (let i = 0; i < l.w * 3; i++) {
      const x = Math.floor(hashN(look.seed, i, 81) * W), y = 5 * CELL + 4 + Math.floor(hashN(look.seed, i, 82) * (3 * CELL - 10));
      if (l.theme.lane === 'flags' && y < 6 * CELL && l.w > 0) continue;
      rect(c, x, y, 2, 1, '#8a8478'); rect(c, x, y + 1, 2, 1, '#4a3a2a');
    }
  }
  // snow banked along the lane's edges, clumps in the ruts
  if (l.theme.snowy) {
    for (let x = 0; x < W; x++) {
      const t = 3 + Math.round(4 * (0.5 + 0.5 * Math.sin(x * 0.07 + look.seed)) + 2 * hashN(look.seed, x >> 2, 1));
      const b = 4 + Math.round(5 * (0.5 + 0.5 * Math.sin(x * 0.05 + look.seed * 3)) + 2 * hashN(look.seed, x >> 2, 2));
      rect(c, x, 5 * CELL, 1, t, '#eef3fc'); rect(c, x, 5 * CELL + t, 1, 1, '#c6d2e4');
      rect(c, x, 8 * CELL - b, 1, b, '#eef3fc'); rect(c, x, 8 * CELL - b, 1, 1, '#ffffff');
    }
    for (let i = 0; i < l.w * 2; i++) {
      const x = Math.floor(hashN(look.seed, i, 91) * W), y = 5 * CELL + 10 + Math.floor(hashN(look.seed, i, 92) * (2 * CELL));
      const w = 3 + Math.floor(hashN(look.seed, i, 93) * 8);
      rect(c, x, y, w, 2, '#e4ebf7'); rect(c, x + 1, y - 1, w - 2, 1, '#f6f9ff');
    }
  }
  // puddles in a wet lane (after rain, or in the fens and the jungle)
  if (['rain', 'storm', 'sleet'].includes(look.weather ?? '') || l.theme.biome === 'wetland' || l.theme.biome === 'jungle' || l.theme.biome === 'underdark') {
    for (let i = 0; i < Math.round(l.w / 2.5); i++) {
      const px = Math.floor(hashN(look.seed, i, 71) * (W - 30)) + 10, py = 5 * CELL + 12 + Math.floor(hashN(look.seed, i, 72) * (2 * CELL - 6));
      const pw = 10 + Math.floor(hashN(look.seed, i, 73) * 18);
      for (let k = 0; k < 4; k++) { const inset = [3, 1, 0, 2][k]!; rect(c, px + inset, py + k, pw - inset * 2, 1, k === 0 ? '#4a3a2a' : '#3c4a5c'); }
      rect(c, px + 3, py + 1, Math.round(pw * 0.4), 1, night ? '#5a6a90' : '#9fb4cc');
    }
  }
  // the signpost where the road comes in
  { const x = Math.round(0.55 * CELL), y = 5 * CELL + 14;
    rect(c, x - 1, y - 28, 4, 30, INK); rect(c, x, y - 27, 2, 28, '#7a4f2c');
    rect(c, x - 4, y - 26, 18, 8, INK); rect(c, x - 3, y - 25, 15, 6, '#a97a4a'); rect(c, x + 12, y - 24, 3, 4, INK); rect(c, x - 1, y - 23, 9, 1, '#5a3a22');
    if (l.theme.snowy) rect(c, x - 4, y - 27, 18, 1, '#f4f7ff'); }
  // a soft shadow along the foot of the facades
  for (const b of l.buildings) rect(c, b.x * CELL, (BASE + 1) * CELL, b.w * CELL, 3, '#00000030');
  for (const b of l.buildings) paintBuilding(c, b, a, look.seed, l.theme.snowy, night);
  // the square's centrepiece: a notice board in the haven, a market stall in a town
  const sq = l.square;
  if (sq.kind === 'board') {
    const x = (sq.x + 0.5) * CELL, y = BASE * CELL - 2;
    rect(c, x, y, 2, 26, INK); rect(c, x + 26, y, 2, 26, INK);
    rect(c, x - 2, y - 2, 32, 18, INK); rect(c, x - 1, y - 1, 30, 16, '#8a5c34');
    for (let k = 0; k < 5; k++) rect(c, x + 1 + k * 6, y + 1 + (k % 2) * 3, 5, 7 + (k % 3), k === 2 ? '#e8d27a' : '#efe7d2');
    rect(c, x - 4, y - 5, 36, 3, INK); rect(c, x - 3, y - 4, 34, 1, '#a9533b');
  } else if (sq.kind === 'stall') {
    const x = sq.x * CELL + 2, y = BASE * CELL - 6, w = 2 * CELL - 4;
    rect(c, x, y, 2, 30, INK); rect(c, x + w - 2, y, 2, 30, INK);
    rect(c, x - 2, y - 2, w + 4, 9, INK);
    for (let k = 0; k < w; k++) rect(c, x + k, y - 1, 1, 7 - (k % 5 === 4 ? 1 : 0), Math.floor(k / 5) % 2 ? '#efe4c8' : '#3e6ea8');
    rect(c, x, y + 19, w, 6, INK); rect(c, x + 1, y + 20, w - 2, 4, '#8a5c34');
    drawItem(c, a.items, 94, x + 4, y + 8); drawItem(c, a.items, 20, x + 22, y + 8);
  }
  const mids = l.props.filter((p) => p.layer === 'mid').sort((p, q) => p.y - q.y);
  const anims: Prop[] = [];
  for (const p of mids) { if (p.anim) anims.push(p); else drawProp(c, a.world, p, 0); }
  for (const lamp of l.lamps) if (lamp.kind === 'post') paintLampPost(c, lamp, l.theme.snowy, false);
  for (const lamp of l.lamps) if (lamp.kind === 'wall') paintWallLamp(c, lamp, false);
  const weatherTint = look.weather === 'storm' ? '#7c8494' : look.weather === 'rain' || look.weather === 'sleet' ? '#a4acb8' : look.weather === 'overcast' ? '#b8c0c8' : look.weather === 'fog' ? '#c4cad0' : look.weather === 'sandstorm' ? (l.theme.biome === 'ashlands' ? '#8a8480' : '#d8b890') : look.weather === 'blizzard' ? '#c8d0dc' : undefined;
  // under the rock the glow-hours are a dim blue-green, never sunlight; the Ashlands' day is red through
  // the ash and their night lit red from below
  const hour = night ? (l.theme.biome === 'ashlands' ? '#8a4a48' : '#5a68b0') : l.theme.biome === 'underdark' ? '#a4d6cf' : l.theme.biome === 'ashlands' ? (weatherTint ?? '#e8b090') : weatherTint ?? (look.weather === 'heat' || l.theme.biome === 'desert' ? '#fff0d8' : '#fff4e6');   // the night, a grey sky, or late-day warmth
  tint(c, hour); tint(fc, hour);
  const frontTint = hour;

  // ── what glows: lit windows, lanterns, the forge's mouth ──
  const glow = document.createElement('canvas'); glow.width = W; glow.height = H;
  const g = glow.getContext('2d')!; g.imageSmoothingEnabled = false;
  for (const b of l.buildings) {
    const f = facadeBox(b);
    if (b.style === 'smithy') { rect(g, b.door * CELL + 6, BASE * CELL + 9, 12, 14, '#c2481c'); rect(g, b.door * CELL + 8, BASE * CELL + 15, 8, 8, '#ff9a3a'); }
    if (!night) continue;
    const lit = (x: number) => windowLit(look.seed, b, x);
    if (b.style === 'temple') {
      for (let k = 0; k < b.w; k++) {
        const cx = f.L + k * CELL + 12;
        if (b.x + k === b.door) { g.fillStyle = '#ffd47a'; g.beginPath(); g.arc(cx, f.wallTop + 10, 4, 0, Math.PI * 2); g.fill(); continue; }
        stainedGlass(g, cx - 3, f.wallTop + 7, 6, f.bottom - f.wallTop - 17, true);
      }
      continue;
    }
    for (const wx of b.windows) if (lit(wx)) paintLitGlass(g, wx * CELL + 7, BASE * CELL + 3, 10, 9, hashN(wx, b.x));
    for (const wx of b.upper) if (lit(wx + 100)) paintLitGlass(g, wx * CELL + 7, f.wallTop + 6, 10, 10, hashN(wx, b.x, 2));
  }
  if (night) {
    for (const lamp of l.lamps) {
      if (lamp.kind === 'wall') paintWallLamp(g, lamp, true);
      if (lamp.kind === 'post') paintLampPost(g, lamp, l.theme.snowy, true);
    }
  }
  return { far, base, glow, anims, front: l.props.filter((p) => p.layer === 'front'), fences: l.fences, snowy: l.theme.snowy, tint: frontTint };
}

/** Boards running across a floor, row by row, with staggered seams and a nail at each. */
function paintPlanks(c: Ctx, x0: number, y0: number, w: number, h: number, seed: number, tones: readonly string[]) {
  for (let y = y0, row = 0; y < y0 + h; y += 6, row++) {
    let x = x0 - Math.floor(hashN(seed, row, 1) * 40);
    for (let k = 0; x < x0 + w; k++) {
      const len = 34 + Math.floor(hashN(seed, row, k, 2) * 40);
      const col = tones[Math.floor(hashN(seed, row, k, 3) * tones.length)]!;
      const bx = Math.max(x0, x), ex = Math.min(x0 + w, x + len);
      rect(c, bx, y, ex - bx, 6, col); rect(c, bx, y, ex - bx, 1, shade(col, 0.14)); rect(c, bx, y + 5, ex - bx, 1, shade(col, -0.45));
      for (let g = bx + 5; g < ex - 4; g += 9 + Math.floor(hashN(seed, g, row) * 9)) rect(c, g, y + 2 + (g % 2), 3, 1, shade(col, -0.16));
      if (x + len < x0 + w) { rect(c, x + len - 1, y, 1, 6, shade(col, -0.5)); rect(c, x + len - 3, y + 2, 1, 1, '#2a1a10'); }
      x += len;
    }
  }
}

/** docs/112: the hanging sign of each workshop (item-atlas icons). */
const SIGNS: Partial<Record<Building['kind'], number>> = { enchanter: 92, hedgewitch: 94, runecarver: 234, stargazer: 93, weaponsmith: 209, armourer: 226, bowyer: 192, leatherworker: 258 };

const roomTint = (real: BuildingKind, night: boolean, kind = STYLE_OF[real]) => (night ? '#8a6a5c' : kind === 'temple' ? '#c8c8e4' : kind === 'smithy' ? '#b89a88' : '#d8b898');

function paintRoom(room: Room, a: Sheets, seed: number, night: boolean): Painted {
  const W = room.w * CELL, H = room.h * CELL;
  const base = document.createElement('canvas'); base.width = W; base.height = H;
  const c = base.getContext('2d')!; c.imageSmoothingEnabled = false;
  const F = room.floor * CELL, T = room.top * CELL;
  // docs/112: a workshop's room is painted as the building it is styled on
  const kind = STYLE_OF[room.kind];
  const stone = kind === 'smithy' || kind === 'temple';
  // ── the rafters above the wall ──
  const dark = kind === 'temple' ? '#16141f' : kind === 'smithy' ? '#120e0e' : '#1a110b';
  rect(c, 0, 0, W, T, dark);
  for (let y = T - 10; y > -24; y -= 18) rect(c, 0, y, W, 2, shade(dark, 0.12));
  for (let x = (room.w % 2) * 12 + 18; x < W; x += CELL * 2) {
    // joists end-on and the struts down to the tie beam
    rect(c, x - 1, 0, 10, T - 8, shade(dark, 0.08)); rect(c, x - 1, 0, 1, T - 8, shade(dark, 0.2));
    for (let i = 0; i < 10; i++) { rect(c, x + 9 + i * 1.6, T - 10 - i * 2, 2, 2, shade(dark, 0.14)); rect(c, x - 3 - i * 1.6, T - 10 - i * 2, 2, 2, shade(dark, 0.14)); }
  }
  // ── the back wall ──
  if (stone) {
    const b: Building = { kind: 'house', style: 'house', name: '', x: 0, w: room.w, floors: 1, roof: 'flat', roofTone: 'slate', wall: 'stone', door: 0, windows: [], upper: [], tone: 0.5 };
    paintWall(c, b, 0, T, W, F, seed);
    c.globalAlpha = kind === 'temple' ? 0.28 : 0.42; rect(c, 0, T, W, F - T, kind === 'temple' ? '#cfd0ea' : '#2a1c16'); c.globalAlpha = 1;
    if (kind === 'temple') {
      // pillars and a dado of darker stone
      for (let x = CELL * 2 + 6; x < W; x += CELL * 4) { rect(c, x - 1, T, 10, F - T, INK); rect(c, x, T, 8, F - T, '#8d8a9c'); rect(c, x + 1, T, 2, F - T, '#b4b0c4'); rect(c, x + 6, T, 2, F - T, '#6a6778'); rect(c, x - 3, T, 14, 5, '#6a6778'); rect(c, x - 3, F - 6, 14, 6, '#6a6778'); }
      rect(c, 0, F - 14, W, 2, '#5f5c6c');
    } else {
      // soot rising from the forge
      const g = c.createLinearGradient(0, T, 0, F); g.addColorStop(0, 'rgba(10,6,4,0.55)'); g.addColorStop(1, 'rgba(10,6,4,0)');
      c.fillStyle = g; c.fillRect(0, T, W, F - T);
    }
  } else {
    // plaster above a panelled wainscot, framed by timber posts
    const b: Building = { kind: 'house', style: 'house', name: '', x: 0, w: room.w, floors: 1, roof: 'flat', roofTone: 'tile', wall: 'plaster', door: 0, windows: [], upper: [], tone: kind === 'inn' ? 0.2 : 0.45 };
    paintWall(c, b, 0, T, W, F, seed);
    c.globalAlpha = kind === 'inn' ? 0.3 : 0.16; rect(c, 0, T, W, F - T, '#7a4a20'); c.globalAlpha = 1;
    const wain = F - 26;
    rect(c, 0, wain, W, F - wain, '#5d3b22'); rect(c, 0, wain, W, 3, '#7d5634'); rect(c, 0, wain + 3, W, 1, INK);
    for (let x = 4; x < W; x += 16) { rect(c, x, wain + 7, 11, F - wain - 11, '#4d3019'); rect(c, x, wain + 7, 11, 1, '#6c4728'); }
    for (let x = CELL * 2 + 9; x < W; x += CELL * 4) { rect(c, x - 1, T, 8, wain - T, INK); rect(c, x, T, 6, wain - T, TIMBER); rect(c, x, T, 2, wain - T, TIMBER_HI); }
  }
  // the tie beam along the wall's top
  rect(c, 0, T - 8, W, 9, INK); rect(c, 0, T - 7, W, 7, stone ? '#4a4652' : TIMBER); rect(c, 0, T - 7, W, 1, stone ? '#6a6678' : TIMBER_HI);
  rect(c, 0, T + 1, W, 4, '#00000040');
  // ── windows ──
  for (const wdw of room.windows) {
    const x = Math.round(wdw.x * CELL) + 3, y = Math.round(wdw.y * CELL) + 2, ww = CELL - 6, wh = wdw.h * CELL - 6;
    if (kind === 'temple') {
      c.fillStyle = INK; c.beginPath(); c.arc(x + ww / 2, y + 6, ww / 2 + 3, Math.PI, 0); c.fill();
      rect(c, x - 3, y + 6, ww + 6, wh - 3, INK);
      c.fillStyle = '#8d8a9c'; c.beginPath(); c.arc(x + ww / 2, y + 6, ww / 2 + 2, Math.PI, 0); c.fill();
      c.fillStyle = INK; c.beginPath(); c.arc(x + ww / 2, y + 6, ww / 2, Math.PI, 0); c.fill();
      continue;   // the glass is lit in the glow pass
    }
    rect(c, x - 3, y - 3, ww + 6, wh + 6, INK);
    rect(c, x - 2, y - 2, ww + 4, wh + 4, stone ? '#5a5660' : TIMBER);
    rect(c, x - 5, y + wh + 2, ww + 10, 3, stone ? '#6a6670' : TIMBER_HI); rect(c, x - 5, y + wh + 5, ww + 10, 1, INK);
  }
  // ── the hearth / forge ──
  if (room.hearth) {
    const hx = room.hearth.x * CELL - 6, hw = 3 * CELL + 12;
    const top = F - 2 * CELL + 2;
    const sb: Building = { kind: 'house', style: 'house', name: '', x: 0, w: 3, floors: 1, roof: 'flat', roofTone: 'tile', wall: 'stone', door: 0, windows: [], upper: [], tone: 0.5 };
    if (kind === 'smithy') {
      // a hood narrowing up into the flue
      rect(c, hx + 22, 0, hw - 44, top - 18, INK); paintWall(c, sb, hx + 23, 0, hx + hw - 23, top - 18, seed + 5);
      for (let i = 0; i < 18; i++) { const inset = 22 - Math.round(i * 1.2); rect(c, hx + inset - 1, top - 18 + i, hw - inset * 2 + 2, 1, INK); rect(c, hx + inset, top - 18 + i, hw - inset * 2, 1, i % 4 === 0 ? '#4a4248' : '#3a3438'); }
    } else {
      rect(c, hx + 10, 0, hw - 20, top, INK);
      paintWall(c, sb, hx + 11, 0, hx + hw - 11, top, seed + 5);
    }
    rect(c, hx, top, hw, F - top + 4, INK);
    paintWall(c, sb, hx + 1, top + 1, hx + hw - 1, F + 4, seed + 9);
    if (kind !== 'smithy') { rect(c, hx - 4, top - 4, hw + 8, 6, INK); rect(c, hx - 3, top - 3, hw + 6, 4, TIMBER_HI); drawItem(c, a.items, 145, hx + 2, top - 18); drawItem(c, a.items, 146, hx + hw - 18, top - 18); }
    // the firebox
    rect(c, hx + 14, top + 14, hw - 28, F - top - 10, INK);
    rect(c, hx + 15, top + 15, hw - 30, F - top - 11, '#1c0d07');
    c.fillStyle = INK; c.beginPath(); c.arc(hx + hw / 2, top + 16, (hw - 28) / 2, Math.PI, 0); c.fill();
    c.fillStyle = '#1c0d07'; c.beginPath(); c.arc(hx + hw / 2, top + 16, (hw - 30) / 2, Math.PI, 0); c.fill();
    if (kind === 'smithy') { // the bellows by the forge
      const bx = hx + hw + 2, by = F - 14;
      rect(c, bx, by, 18, 10, INK); rect(c, bx + 1, by + 1, 16, 8, '#6b4a2e'); rect(c, bx + 1, by + 4, 16, 1, '#4a3020'); rect(c, bx + 17, by + 3, 6, 3, INK);
    }
  }
  // ── shelves on the wall ──
  for (const sh of room.shelves) {
    const x = Math.round(sh.x * CELL), y = Math.round(sh.y * CELL), w = sh.w * CELL - 6;
    rect(c, x - 1, y - 1, w + 2, 5, INK); rect(c, x, y, w, 3, '#8a5c34'); rect(c, x, y, w, 1, '#a97a4a');
    for (const bx of [x + 4, x + w - 7]) { rect(c, bx, y + 4, 3, 5, INK); rect(c, bx + 1, y + 4, 1, 4, '#6b4a2e'); }
  }
  // ── docs/142: the dyer's lines of dyed cloth across the back wall ──
  for (const ln of room.dyer?.lines ?? []) {
    const x0 = Math.round(ln.x * CELL), y = Math.round(ln.y * CELL), w = ln.w * CELL;
    rect(c, x0 - 2, y - 1, 3, 4, INK); rect(c, x0 + w - 1, y - 1, 3, 4, INK);
    c.fillStyle = '#2a1a10'; for (let x = x0; x < x0 + w; x++) c.fillRect(x, y + Math.round(Math.sin(((x - x0) / w) * Math.PI) * 4), 1, 1);
    for (let k = 0, x = x0 + 3; x + 10 < x0 + w; x += 13, k++) {
      const ramp = DYES[(ln.from - 1 + k) % DYES.length]!.ramp.map((h) => `#${h}`);
      const sag = Math.round(Math.sin(((x + 5 - x0) / w) * Math.PI) * 4), h = 16 + ((k * 7) % 3) * 4;
      rect(c, x - 1, y + sag, 12, h + 1, INK);
      rect(c, x, y + sag + 1, 10, h - 1, ramp[2]!); rect(c, x, y + sag + 1, 3, h - 1, ramp[1]!); rect(c, x + 7, y + sag + 1, 1, h - 1, ramp[3]!);
      rect(c, x, y + sag + h - 3, 10, 2, ramp[1]!);              // the hem
      rect(c, x + 4, y + sag - 1, 2, 2, '#d8c7a3');              // the peg
    }
  }
  // ── the floor ──
  if (kind === 'inn' || kind === 'shop') paintPlanks(c, 0, F, W, H - F, seed, kind === 'inn' ? ['#6e4a2c', '#7a5232', '#664428', '#835a37'] : ['#86603c', '#7a5634', '#90683f', '#80593a']);
  else if (kind === 'smithy') {
    // packed earth and worn flags, black with soot near the forge
    rect(c, 0, F, W, H - F, '#2a221e');
    for (let y = F, row = 0; y < H; y += 9, row++) {
      let x = -Math.floor(hashN(seed, row, 4) * 12);
      for (let k = 0; x < W; k++) {
        const sw = 11 + Math.floor(hashN(seed, row, k, 5) * 12);
        const v = hashN(seed, row, k, 6);
        const col = v < 0.3 ? '#4a423c' : v < 0.65 ? '#575049' : v < 0.9 ? '#625a52' : '#6e5a44';
        rect(c, x + 1, y + 1, sw - 2, 7, col); rect(c, x + 1, y + 1, sw - 2, 1, shade(col, 0.15)); rect(c, x + 1, y + 7, sw - 2, 1, shade(col, -0.25));
        x += sw;
      }
    }
  }
  else for (let y = F; y < H; y += 12) for (let x = ((y - F) / 12) % 2 ? -6 : 0; x < W; x += 12) { const v = hashN(seed, x, y); rect(c, x, y, 12, 12, INK); rect(c, x + 1, y + 1, 11, 11, ((x + y) / 12) % 2 ? '#a8a4b6' : '#8e8a9e'); if (v < 0.2) rect(c, x + 3, y + 4, 4, 1, '#7a7688'); rect(c, x + 1, y + 1, 11, 1, '#bcb8ca'); }
  // the rug
  if (room.rug) {
    const g = room.rug.blue ? [[1377, 1378, 1379], [1434, 1435, 1436], [1491, 1492, 1493]] : [[1374, 1375, 1376], [1431, 1432, 1433], [1488, 1489, 1490]];
    for (let j = 0; j < room.rug.h; j++) for (let i = 0; i < room.rug.w; i++) {
      const row = j === 0 ? 0 : j === room.rug.h - 1 ? 2 : 1, col = i === 0 ? 0 : i === room.rug.w - 1 ? 2 : 1;
      drawWorld(c, a.world, g[row]![col]!, (room.rug.x + i) * CELL, (room.rug.y + j) * CELL);
    }
  }
  // docs/116: more rugs (the inn's hearth rug)
  for (const rr of room.rugs ?? []) {
    const g = rr.blue ? [[1377, 1378, 1379], [1434, 1435, 1436], [1491, 1492, 1493]] : [[1374, 1375, 1376], [1431, 1432, 1433], [1488, 1489, 1490]];
    for (let j = 0; j < rr.h; j++) for (let i = 0; i < rr.w; i++) drawWorld(c, a.world, g[j === 0 ? 0 : j === rr.h - 1 ? 2 : 1]![i === 0 ? 0 : i === rr.w - 1 ? 2 : 1]!, (rr.x + i) * CELL, (rr.y + j) * CELL);
  }
  // depth: the floor darkens away from the light at the front, the wall's foot is in shadow
  { const g = c.createLinearGradient(0, F, 0, H); g.addColorStop(0, 'rgba(0,0,0,0.42)'); g.addColorStop(0.35, 'rgba(0,0,0,0.08)'); g.addColorStop(1, 'rgba(0,0,0,0.25)'); c.fillStyle = g; c.fillRect(0, F, W, H - F); }
  // ── the way out: a door in the back wall, bottom left ──
  const dx = room.exit * CELL;
  rect(c, dx - 4, F - CELL - 6, CELL + 8, CELL + 7, INK); rect(c, dx - 3, F - CELL - 5, CELL + 6, CELL + 6, stone ? '#6a6670' : TIMBER);
  drawWorld(c, a.world, kind === 'temple' ? PROPS.doors.blue : PROPS.doors.wood2, dx, F - CELL + 1);
  // ── the pews ──
  // ── the furniture ──
  const anims: Prop[] = [];
  for (const p of room.props.filter((q) => q.layer === 'back')) drawProp(c, a.world, p, 0);
  for (const it of room.items.filter((i) => i.y < room.floor)) drawItem(c, a.items, it.item, it.x * CELL, it.y * CELL);
  for (const p of room.props.filter((q) => q.layer === 'mid').sort((p, q) => p.y - q.y)) { if (p.anim) anims.push(p); else drawProp(c, a.world, p, 0); }
  // docs/142: the dyer's vats, each a wooden tub brimming with its dye, seen a little from above
  for (const v of room.dyer?.vats ?? []) {
    const ramp = DYES.find((d) => d.id === v.dye)!.ramp.map((h) => `#${h}`);
    const cx = Math.round(v.x * CELL) + 12, top = Math.round(v.y * CELL) + 2;
    const ell = (y0: number, rx: number, ry: number, fill: (row: number) => string) => {
      for (let i = -ry; i <= ry; i++) { const hw = Math.round(rx * Math.sqrt(Math.max(0, 1 - (i / (ry + 0.5)) ** 2))); rect(c, cx - hw, y0 + ry + i, hw * 2, 1, fill(i)); }
    };
    // the staves, narrowing a little to the floor, with two iron hoops
    for (let y = top + 4; y < top + 21; y++) { const hw = 11 - Math.floor((y - top - 4) / 9); rect(c, cx - hw - 1, y, hw * 2 + 2, 1, INK); rect(c, cx - hw, y, hw * 2, 1, (y - top) % 9 === 7 || (y - top) % 9 === 8 ? '#3e3a36' : '#7a4e2a'); rect(c, cx - hw, y, 3, 1, '#5c3a1e'); }
    for (let k = -6; k <= 6; k += 6) rect(c, cx + k, top + 9, 1, 11, '#5c3a1e');
    rect(c, cx - 10, top + 21, 20, 1, INK);
    // the rim and the dye inside it
    ell(top, 12, 4, () => INK);
    ell(top + 1, 11, 3, (i) => (i < 0 ? '#a0703e' : '#8a5c34'));
    ell(top + 2, 9, 2, (i) => (i <= -1 ? ramp[2]! : ramp[1]!));
    rect(c, cx - 5, top + 3, 4, 1, ramp[3]!); rect(c, cx + 2, top + 4, 2, 1, ramp[2]!);
    // a paddle left in it
    rect(c, cx + 4, top - 8, 3, 13, INK); rect(c, cx + 5, top - 7, 1, 11, '#d8c7a3');
  }
  if (kind === 'smithy') {
    // the anvil before the forge
    const ax = (Math.floor(room.w / 2) - 0.6) * CELL, ay = F + CELL + 8;
    rect(c, ax, ay, 20, 6, INK); rect(c, ax + 1, ay + 1, 18, 4, '#5a5f6a'); rect(c, ax + 1, ay + 1, 18, 1, '#8c93a0'); rect(c, ax - 3, ay + 1, 4, 3, INK);
    rect(c, ax + 6, ay + 6, 8, 6, INK); rect(c, ax + 7, ay + 6, 6, 5, '#454a54'); rect(c, ax + 3, ay + 12, 14, 4, INK); rect(c, ax + 4, ay + 12, 12, 3, '#3d424c');
  }
  // the hanging lights' chains (their flames glow below)
  for (const hg of room.hangs) { const x = Math.round(hg.x * CELL); rect(c, x, 0, 1, T - 2, '#3a3438'); }
  tint(c, roomTint(kind, night));

  // ── what glows: the fire, the lanterns, the windows ──
  const glow = document.createElement('canvas'); glow.width = W; glow.height = H;
  const g = glow.getContext('2d')!; g.imageSmoothingEnabled = false;
  if (room.hearth) {
    const hx = room.hearth.x * CELL - 6, hw = 3 * CELL + 12;
    rect(g, hx + 17, F - 12, hw - 34, 9, '#a8380e'); rect(g, hx + 21, F - 7, hw - 42, 4, '#ff9a3a');
  }
  for (const wdw of room.windows) {
    const x = Math.round(wdw.x * CELL) + 3, y = Math.round(wdw.y * CELL) + 2, ww = CELL - 6, wh = wdw.h * CELL - 6;
    if (kind === 'temple') {
      g.save(); g.beginPath(); g.arc(x + ww / 2, y + 6, ww / 2 - 1, Math.PI, 0); g.rect(x, y + 6, ww, wh - 4); g.clip();
      stainedGlass(g, x, y - 6, ww, wh + 6, !night);
      if (night) { g.globalAlpha = 0.6; rect(g, x, y - 6, ww, wh + 6, '#101830'); g.globalAlpha = 1; }
      g.restore();
      rect(g, x + Math.floor(ww / 2), y, 1, wh, INK);
      continue;
    }
    if (night) { rect(g, x, y, ww, wh, '#0e1430'); rect(g, x + 4, y + 5, 1, 1, '#e0e6ff'); rect(g, x + 13, y + 10, 1, 1, '#c8d2ff'); rect(g, x + 8, y + 15, 1, 1, '#aab4e8'); }
    else { rect(g, x, y, ww, wh, '#b8d8f0'); rect(g, x, y + wh - 8, ww, 8, '#86b47a'); rect(g, x, y + wh - 10, ww, 2, '#a8c896'); rect(g, x + 2, y + 3, 4, 1, '#ffffff'); }
    rect(g, x + Math.floor(ww / 2), y, 1, wh, INK); rect(g, x, y + Math.floor(wh / 2), ww, 1, INK);
  }
  for (const hg of room.hangs) {
    const x = Math.round(hg.x * CELL), y = T - 2;
    if (hg.kind === 'lantern') paintLantern(g, x - 3, y, true);
    else {
      rect(g, x - 13, y + 4, 27, 4, INK); rect(g, x - 12, y + 5, 25, 2, '#6b4a2e');
      for (const k of [-10, -3, 4, 10]) { rect(g, x + k, y, 2, 4, '#efe6cc'); rect(g, x + k, y - 3, 2, 2, '#ffcf5a'); rect(g, x + k, y - 4, 1, 1, '#fff3c0'); }
      rect(g, x - 1, y - 10, 1, 6, '#3a3438');
    }
  }
  // shelves' goods sit over the tint's shadow a little brighter
  for (const it of room.items.filter((i) => i.y >= room.floor)) drawItem(g, a.items, it.item, it.x * CELL, it.y * CELL);
  return { base, glow, anims, front: room.props.filter((p) => p.layer === 'front'), pews: room.pews };
}

/**
 * docs/121 VW6: the whole street as one picture (the far country, the street, what glows and what stands
 * in front), the way the stage draws it, for the inn's window to look out on. Atlas pixels; the
 * `FAR_HEAD` rows of sky over the far country are clear.
 */
export function streetPicture(l: VillageLayout, look: VillageLook, a: Sheets): HTMLCanvasElement {
  const p = paintStreet(l, look, a);
  const out = document.createElement('canvas'); out.width = l.w * CELL; out.height = (FAR_HEAD + l.h) * CELL;
  const c = out.getContext('2d')!; c.imageSmoothingEnabled = false;
  if (p.far) c.drawImage(p.far, 0, 0);
  c.save(); c.translate(0, FAR_HEAD * CELL);
  c.drawImage(p.base, 0, 0);
  for (const q of p.anims) drawProp(c, a.world, q, 0);
  c.drawImage(p.glow, 0, 0);
  const f = document.createElement('canvas'); f.width = out.width; f.height = l.h * CELL;
  const fc = f.getContext('2d')!; fc.imageSmoothingEnabled = false;
  if (p.fences) paintFence(fc, p.fences, VERGE * CELL, !!p.snowy);
  for (const q of p.front) drawProp(fc, a.world, q, 0);
  if (p.tint) tint(fc, p.tint);
  c.drawImage(f, 0, 0);
  c.restore();
  return out;
}

/** Temple pews: dark wooden benches, drawn over the people sitting in them. */
function paintPews(c: Ctx, pews: Array<{ x: number; y: number; w: number }>) {
  for (const p of pews) {
    const x = Math.round(p.x * CELL), y = Math.round(p.y * CELL), w = p.w * CELL - 4;
    rect(c, x - 1, y - 1, w + 2, 8, INK); rect(c, x, y, w, 6, '#5a3a24'); rect(c, x, y, w, 1, '#7a5634');
    rect(c, x - 1, y + 7, w + 2, 6, INK); rect(c, x, y + 8, w, 4, '#4a2e1c'); rect(c, x, y + 8, w, 1, '#6b4a2e');
    for (const lx of [x + 2, x + w - 5, x + Math.round(w / 2)]) { rect(c, lx - 1, y + 13, 5, 8, INK); rect(c, lx, y + 13, 3, 7, '#3e2616'); }
  }
}

// ---- sizing ------------------------------------------------------------------------------------
function useViewport(): { w: number; h: number } {
  const [v, setV] = useState(() => ({ w: typeof window === 'undefined' ? 1400 : window.innerWidth, h: typeof window === 'undefined' ? 860 : window.innerHeight }));
  useEffect(() => {
    const on = () => setV({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return v;
}

const DEAL_ICON = { hire: 199, secret: 138, trade: 72 } as const;
const DEAL_WORD = { hire: 'for hire', secret: 'knows a secret', trade: 'trades' } as const;

// ---- the sky -------------------------------------------------------------------------------------
function starField(n: number, seed: number) {
  return Array.from({ length: n }, (_, i) => ({ left: hashN(seed, i, 1) * 100, top: Math.pow(hashN(seed, i, 2), 1.5) * 92, size: i % 9 === 0 ? 3 : 2, tw: i % 4 === 0, delay: (i % 11) * 0.45 }));
}
const flakes = (n: number, seed: number, size: [number, number], dur: [number, number]) => Array.from({ length: n }, (_, i) => {
  const s = i % 5 === 0 ? size[1] : size[0];
  return { left: `${(hashN(seed, i) * 112 - 6).toFixed(2)}%`, animationDelay: `${(-hashN(seed, i, 7) * dur[1]).toFixed(2)}s`, animationDuration: `${(dur[0] + ((i * 7) % 10) / 10 * (dur[1] - dur[0])).toFixed(2)}s`, width: s, height: s } as CSSProperties;
});
/** Pixel clouds on a strip twice the window wide, drifting by transform (one canvas, crisp). */
export function Clouds({ seed, scale, dark }: { seed: number; scale: number; dark: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const W = 1024, H = 90;
  useEffect(() => {
    const c = ref.current?.getContext('2d');
    if (!c) return;
    c.clearRect(0, 0, W, H);
    const body = dark ? '#7d8696' : '#f4f7fb', belly = dark ? '#5e6676' : '#cfd9e6', edge = dark ? '#98a0ae' : '#ffffff';
    // each cloud is a union of puffs standing on a flat base; lit along its top, shaded along its belly
    const n = dark ? 14 : 12;
    for (let i = 0; i < n; i++) {
      const cx = ((i + hashN(seed, i, 1) * 0.8) / n) * W, base = 24 + hashN(seed, i, 2) * (dark ? 50 : 58);
      const puffs = (dark ? 5 : 3) + Math.floor(hashN(seed, i, 4) * 3), r0 = (dark ? 10 : 7) + hashN(seed, i, 3) * (dark ? 9 : 8);
      const span = puffs * r0 * 0.95;
      const top = new Map<number, number>();
      for (let k = 0; k < puffs; k++) {
        const px = cx - span / 2 + (k + 0.5) * (span / puffs), rr = r0 * (0.5 + 0.6 * Math.sin(((k + 0.5) / puffs) * Math.PI)) * (0.75 + hashN(seed, i, k) * 0.5);
        for (let dx = -Math.floor(rr); dx <= rr; dx++) {
          const x = Math.round(px + dx), hgt = Math.round(Math.sqrt(Math.max(0, rr * rr - dx * dx)) * (dark ? 0.8 : 1.05));
          top.set(x, Math.min(top.get(x) ?? 999, Math.round(base - hgt)));
        }
      }
      for (const [x, t] of top) for (const dx of [0, W, -W]) {
        const xx = x + dx;
        if (xx < 0 || xx >= W) continue;
        c.fillStyle = body; c.fillRect(xx, t, 1, Math.round(base) - t);
        c.fillStyle = edge; c.fillRect(xx, t, 1, Math.min(2, Math.round(base) - t));
        c.fillStyle = belly; c.fillRect(xx, Math.round(base) - 3, 1, 3);
      }
    }
  }, [seed, dark]);
  // two copies side by side: the band slides one copy's width and repeats seamlessly
  const copy = useRef<HTMLCanvasElement>(null);
  useEffect(() => { const c = copy.current?.getContext('2d'); if (c && ref.current) { c.clearRect(0, 0, W, H); c.drawImage(ref.current, 0, 0); } }, [seed, dark]);
  const st = { width: W * scale, height: H * scale } as CSSProperties;
  return <><canvas ref={ref} className="fk-village__clouds" width={W} height={H} style={st} aria-hidden="true" /><canvas ref={copy} className="fk-village__clouds" width={W} height={H} style={st} aria-hidden="true" /></>;
}

// ---- the view ------------------------------------------------------------------------------------
export interface VillageViewProps {
  look: VillageLook;
  spot: VillageSpot;
  npcs: VillageNpc[];
  heroes: VillageHero[];
  talking?: string;
  /** Called once the band has walked over to the villager (docs/64 VP2). */
  onTalk(key: string): void;
  onEnter(kind: BuildingKind): void;
  onLeave(): void;
  /** docs/64 VP2: walk over to this villager and talk (the cry for help, the counter's keeper). */
  summon?: VillageSummon;
  /** docs/64 VP4: the band's meal at the inn while it lasts; the view says how it is going. */
  meal?: VillageMeal;
  onMeal?(stage: MealStage): void;
  /** docs/73 TP2: where my band's leader has walked to (and whether it sits at the table), for the other players' views. */
  onStand?(stand: VillageStand): void;
  /** docs/111 TB1: a hero in the street (mine or another player's) clicked: their stats card. */
  onHero?(id: bigint): void;
  /** docs/116: the inn's rows and what can be asked for there (the inn and the room upstairs). */
  inn?: InnData;
  /** docs/120 VM2: who I am (an owner's hex), the other players' figures in this room, and where mine are (to tell them). */
  me?: string;
  remote?: RemoteFigures[];
  onFigures?(figures: VillageFigure[], talkNpc: string | undefined): void;
  /** docs/120 VM4: the seats and stations held in this room, and holding mine (false: someone was there first). */
  held?: HeldSeat[];
  onSit?(seats: Array<{ seat: number; heroId: bigint }>): Promise<boolean>;
  /** docs/120 VM5: the room I came out of (my band steps out of its door into the street). */
  cameFrom?: VillageSpot;
  /** Say something to the player (a seat someone else got to first). */
  onNote?(text: string): void;
  /** docs/121 VW3: the meals ordered in this village (each hero's, mine and others'), and when (server ms). */
  meals?: Array<{ heroKey: string; atMs: number }>;
  children?: ReactNode;
}

/** The bat's loop across the night sky (s, `village.css`). */
const BAT_S = 11;
/** docs/64 VP2: how fast the band walks (cells a second), and how slowly villagers amble. */
const WALK_SPEED = 3.4, AMBLE_SPEED = 0.5;

const PLACE: Record<VillageSpot, Place['key']> = {
  street: 'place.town', inn: 'place.tavern', upstairs: 'place.tavern', shop: 'place.shop', smithy: 'place.smithy', temple: 'place.study',
  // docs/112: each workshop sounds like the building it is styled on (the witch's cottage a quiet study)
  enchanter: 'place.study', stargazer: 'place.study', hedgewitch: 'place.study', runecarver: 'place.smithy', weaponsmith: 'place.smithy', armourer: 'place.smithy', bowyer: 'place.shop', leatherworker: 'place.shop', dyer: 'place.shop',
};
const INSIDE_NAME: Record<BuildingKind, string> = {
  inn: 'Inn', upstairs: 'Room upstairs', shop: 'Shop', smithy: 'Smithy', temple: 'Temple',
  enchanter: "Enchanter's Tower", hedgewitch: "Hedge-Witch's Cottage", runecarver: "Runecarver's Hall", stargazer: "Stargazer's Observatory",
  weaponsmith: "Bladewright's Forge", armourer: "Armourer's Yard", bowyer: 'Bowyer & Fletcher', leatherworker: 'Tanner & Tailor', dyer: "Dyer's Yard",
};

/** Where each villager on this spot stands: seeded street spots, or each room's posts and seats. */
function placeNpcs(npcs: VillageNpc[], spot: VillageSpot, street: VillageLayout | undefined, room: Room | undefined, seed: number): Map<string, Station> {
  const at = npcs.filter((n) => n.at === spot);
  const out = new Map<string, Station>();
  if (street) {
    const spots = [...street.spots].sort((a, b) => hashN(seed, a, 61) - hashN(seed, b, 61));
    // keep them a cell apart where the street allows
    const chosen: number[] = [];
    for (const s of spots) { if (chosen.length >= at.length) break; if (chosen.every((c) => Math.abs(c - s) >= 2) || spots.length - chosen.length < at.length) chosen.push(s); }
    for (const s of spots) { if (chosen.length >= at.length) break; if (!chosen.includes(s)) chosen.push(s); }
    at.forEach((n, i) => {
      const x = chosen[i] ?? street.spots[i % Math.max(1, street.spots.length)] ?? 6;
      const y = hashN(seed, x, 3) < 0.5 ? 5.1 : 5.7;
      out.set(n.key, { x, y, flip: hashN(seed, x, 9) < 0.5 });
    });
    return out;
  }
  if (room) {
    const kind = room.kind;
    const words = KEEPER_WORDS[kind];
    const keeperIdx = Math.max(0, words ? at.findIndex((n) => words.test(n.epithet)) : 0);
    const rest = at.filter((_, i) => i !== keeperIdx);
    if (at[keeperIdx]) out.set(at[keeperIdx]!.key, room.keeper);
    // docs/73 TP4: who sits where at a meal is `seatPlan`'s; here everyone has their usual post
    const spare = (i: number) => ({ x: Math.min(room.w - 3, room.band.x + 5 + i * 1.6), y: room.floor + 3 });
    rest.forEach((n, i) => out.set(n.key, room.others[i] ?? spare(i - room.others.length)));
  }
  return out;
}

export function VillageView({ look, spot, npcs, heroes, talking, onTalk, onEnter, onLeave, summon, meal, onMeal, onStand, onHero, inn, me = '', remote, onFigures, held, onSit, cameFrom, onNote, meals, children }: VillageViewProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const vp = useViewport();
  const [sheets, setSheets] = useState<Sheets>();
  const [phase, setPhase] = useState(0);
  const [ready, setReady] = useState(false);
  useEffect(() => { let live = true; void loadAll().then((a) => { if (live) setSheets(a); }); return () => { live = false; }; }, []);
  useEffect(() => {
    if (reduced) return;
    const t = window.setInterval(() => setPhase((p) => 1 - p), 420);
    return () => window.clearInterval(t);
  }, [reduced]);
  // docs/116 CI7: the room's own sound sinks under the bard's tune; upstairs it is the inn heard through the floor
  const tuneOn = !!useInn((s) => s.playing);
  usePlace({ key: PLACE[spot], ...(spot === 'street' ? { band: look.night ? 1 : 0 } : {}), ...(spot === 'upstairs' ? { gain: 0.3 } : spot === 'inn' && tuneOn ? { gain: 0.55 } : {}) });

  const inside = isInside(spot);
  // docs/120 VM5: one street and one room size for every player (a place in cells is the same place in
  // every view); a whole-number scale for the window, and the camera covers the rest
  const cols = inside ? ROOM_COLS : streetCols(look.size, look.buildings.length);
  const scale = stageScale(cols, inside ? ROOM_ROWS : STREET_H, vp, inside);
  const cellPx = CELL * scale;
  // docs/121 VW7: the street runs on into the countryside either side, to pan and zoom out over
  const street = useMemo(() => (inside ? undefined : villageLayout(look, cols, OUTSKIRTS)), [inside, look, cols]);
  const edge = street?.margin ?? 0;
  const room = useMemo(() => (inside ? roomLayout(spot, look.seed, ROOM_COLS, ROOM_ROWS, ROOM_FLOOR_ROWS) : undefined), [inside, spot, look.seed]);
  const gw = street?.w ?? room!.w, gh = street?.h ?? room!.h;
  const painted = useMemo(() => (sheets ? (street ? paintStreet(street, look, sheets) : paintRoom(room!, sheets, look.seed, look.night)) : undefined), [sheets, street, room, look]);

  const baseRef = useRef<HTMLCanvasElement>(null);
  const farRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = farRef.current?.getContext('2d');
    if (!c || !painted?.far) return;
    c.clearRect(0, 0, c.canvas.width, c.canvas.height);
    c.drawImage(painted.far, 0, 0);
  }, [painted]);
  const frontRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = baseRef.current?.getContext('2d');
    if (!c || !painted || !sheets) return;
    c.imageSmoothingEnabled = false;
    c.clearRect(0, 0, c.canvas.width, c.canvas.height);
    c.drawImage(painted.base, 0, 0);
    for (const p of painted.anims) drawProp(c, sheets.world, p, phase);
    c.drawImage(painted.glow, 0, 0);
    setReady(true);
  }, [painted, sheets, phase]);
  // the front layer (fences, pews, the counter, the shaded foreground) does not move: painted once
  useEffect(() => {
    const f = frontRef.current?.getContext('2d');
    if (!f || !painted || !sheets) return;
    f.imageSmoothingEnabled = false;
    f.clearRect(0, 0, f.canvas.width, f.canvas.height);
    if (painted.fences) paintFence(f, painted.fences, VERGE * CELL, !!painted.snowy);
    if (painted.pews) paintPews(f, painted.pews);
    const near = (p: Prop) => !inside && p.y >= VERGE + 2;
    for (const p of painted.front) if (!near(p)) drawProp(f, sheets.world, p, 0);
    // what stands right in front of the eye is in shade
    hazeOver(f, look.night ? '#02040a' : '#0f1a12', look.night ? 0.55 : 0.4, (o) => { for (const p of painted.front) if (near(p)) drawProp(o, sheets.world, p, 0); }, f.canvas.width, f.canvas.height);
    if (inside) tint(f, roomTint(spot, look.night));
    else if (painted.tint) tint(f, painted.tint);
  }, [painted, sheets, look.night, inside, spot]);

  const stageW = gw * cellPx, stageH = gh * cellPx;
  const at = (x: number, y: number): CSSProperties => ({ left: Math.round(x * cellPx), top: Math.round(y * cellPx) });
  const mealOn = !!meal && !!room?.table;
  const grid = useMemo<WalkGrid>(() => (street ? streetGrid(street) : roomGrid(room!)), [street, room]);
  const usual = useMemo(() => placeNpcs(npcs, spot, street, room, look.seed), [npcs, spot, street, room, look.seed]);
  const here = npcs.filter((n) => n.at === spot);
  // docs/73 TP2: my heroes walk; another player's stand where their band's leader has walked to
  const mineH = heroes.filter((h) => h.mine !== false);
  const crowd = useMemo(() => {
    const by = new Map<string, VillageHero[]>();
    for (const h of heroes) if (h.mine === false) by.set(h.owner ?? '?', [...(by.get(h.owner ?? '?') ?? []), h]);
    return [...by.entries()].map(([owner, hs]) => ({ owner, heroes: hs, stand: hs[0]?.stand }));
  }, [heroes]);
  // docs/120 VM4: the tables' seats are held one sitter each (`village_seat`): every view seats each band
  // where its player sat it. A villager whose stool someone holds gets up and stands aside; the rest keep
  // their stools. My meal takes free seats (villagers' stools only when it must, the long table's first).
  const tSeats = useMemo(() => (room ? tableSeats(room) : []), [room]);
  const seatMap = useMemo(() => (room ? seatsOf(room, tSeats) : new Map<number, Station>()), [room, tSeats]);
  const [myTable, setMyTable] = useState<number[]>([]);
  const heldSig = (held ?? []).map((h) => `${h.seat}:${h.owner}`).sort().join();
  const othersHeld = useMemo(() => new Set((held ?? []).filter((h) => h.owner !== me).map((h) => h.seat)), [heldSig, me]);
  const tableHeld = useMemo(() => [...othersHeld, ...myTable.map((i) => SEAT.table + i)].filter((c) => c >= SEAT.table && c < SEAT.table + 100).map((c) => tSeats[c - SEAT.table]).filter((x): x is Seat => !!x), [othersHeld, myTable, tSeats]);
  const evicted = useMemo(() => here.filter((n) => { const st = usual.get(n.key); return !!st?.seated && tableHeld.some((t) => Math.abs(t.x - st.x) < SEAT_GAP && Math.abs(t.y - st.y) < 0.5); }).map((n) => n.key),
    [here.map((n) => n.key).join(), usual, tableHeld]);
  const places = useMemo(() => {
    if (!room?.table || !evicted.length) return usual;
    const out = new Map(usual);
    const taken: Pt[] = [...[...usual.entries()].filter(([k]) => !evicted.includes(k)).map(([, st]) => st), ...tableHeld, room.band];
    const spots = asideSpots(grid, taken, { x: room.table.x + room.table.w + 1.5, y: grid.y0 }, evicted.length);
    evicted.forEach((k, i) => out.set(k, { ...spots[i]!, flip: true }));
    return out;
  }, [usual, evicted.join(), tableHeld, room, grid]);
  const heldRef = useRef(othersHeld);
  heldRef.current = othersHeld;
  const usualRef = useRef(usual);
  usualRef.current = usual;
  const season = look.season;
  const biome = (look.biome ?? 'temperate').toLowerCase();
  const weather = inside ? undefined : look.weather;
  const night = look.night;
  const clouded = weather !== undefined && !['clear', 'heat', 'frost'].includes(weather);
  const dressing = inside || biome === 'desert' || biome === 'jungle' || biome === 'underdark' || biome === 'ashlands' ? undefined : season === 'winter' && weather !== 'snow' && weather !== 'blizzard' ? 'snow' : season === 'autumn' ? 'leaves' : season === 'spring' ? 'petals' : undefined;
  const stars = useMemo(() => starField(night && !clouded && biome !== 'underdark' && biome !== 'ashlands' ? 110 : 0, look.seed), [night, clouded, biome, look.seed]);
  // glow-worms in the Underdark's deep dark, but in the deep still; nothing glows in the Ashlands' air but embers
  const fireflies = !inside && night && !clouded && biome !== 'desert' && biome !== 'ashlands' && (season === 'summer' || (biome === 'underdark' && season !== 'winter'));
  const motion = !reduced;

  // docs/120 VM2: where another player's figure is now, by its key (from their `village_figures` row)
  const remoteFig = useMemo(() => {
    const out = new Map<string, { f: VillageFigure; atMs: number; owner: string }>();
    for (const r of remote ?? []) for (const f of r.figures) out.set(figureKey(f.heroId, f.beast), { f, atMs: r.atMs, owner: r.owner });
    return out;
  }, [remote]);
  const remoteNow = (k: string): Pt | undefined => { const r = remoteFig.get(k); return r ? figureAt(r.f, (smoothServerNow() - r.atMs) / 1000).at : undefined; };
  // where each figure of the band first stands: mine just in from the edge, or out of the door I came
  // out of (docs/120), clear of whoever stands there; each other player's where their row says, else
  // where their band's leader last walked to, else a little further in
  const doorOut = street && cameFrom && cameFrom !== 'street' ? street.doors.find((d) => d.kind === (cameFrom === 'upstairs' ? 'inn' : cameFrom)) : undefined;
  // (only a figure not yet placed reads these, so they are worked out once a room, a band and what the others told)
  const remoteSig = (remote ?? []).map((r) => `${r.owner}@${r.atMs}`).join('|');
  const heroStations: Station[] = useMemo(() => {
    const taken: Pt[] = heroes.filter((h) => h.mine === false).map((h) => remoteNow(`h:${h.key}`)).filter((p): p is Pt => !!p);
    return heroes.map((h) => {
      if (h.mine !== false) {
        const i = mineH.indexOf(h);
        const want = street ? (doorOut ? { x: doorOut.door + 0.1 + (i % 2 ? 1 : -1) * Math.ceil(i / 2) * 0.8, y: 5.3 + (i % 2) * 0.3 } : { x: edge + 0.6 + i * (mineH.length > 4 ? 3.6 / mineH.length : 0.95), y: 6.2 + (i % 2) * 0.35 })
          : { x: room!.band.x + i * 0.95, y: room!.band.y + (i % 2) * 0.3 };
        const st = taken.length ? clearSpot(grid, want, taken) : want;
        taken.push(st);
        return st;
      }
      const now = remoteNow(`h:${h.key}`);
      if (now) return now;
      const g = crowd.findIndex((c) => c.heroes.includes(h)), i = crowd[g]!.heroes.indexOf(h), st = crowd[g]!.stand;
      if (st && st.x >= 0) return nearestFree(grid, { x: st.x - i * FILE_GAP, y: st.y });
      return nearestFree(grid, street ? { x: 5 + g * 3.5 + i * 0.95, y: 5.6 + (i % 2) * 0.35 } : { x: room!.band.x + 4 + g * 3.5 + i * 0.95, y: room!.band.y });
    });
  }, [grid, heroes.map((h) => h.key).join(), remoteSig, doorOut?.door, crowd]);
  // ---- docs/64: the camera, and people on the move ------------------------------------------------
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  // docs/120 VM5: the stage (one size for everyone) covers the window; the street is framed on my band
  const lead0 = heroStations[heroes.findIndex((h) => h.mine !== false)];
  // docs/121 VW7: home frames the village itself; the camera can be panned and zoomed out over the
  // countryside either side, never past the drawn street's edges
  useOverworldCamera(rootRef, stageRef, { skip: '.fk-village__overlays *', resetKey: spot, cover: { axes: inside ? 'wh' : 'w', ...(street && lead0 ? { focus: { x: (lead0.x + 0.5) * cellPx } } : {}),
    ...(street ? { frame: { x0: edge * cellPx, x1: (edge + cols) * cellPx }, lock: { above: FAR_HEAD * cellPx } } : {}) } });
  const mover = useMotion(cellPx);
  // a new room (or a new street width) starts everyone afresh where the layout puts them
  const sceneKey = `${spot}:${gw}x${gh}`;
  const scene = useRef('');
  const [passers, setPassers] = useState<Passer[]>([]);
  // (a hireling's old place too: they step out of it only in the room they were hired in)
  if (scene.current !== sceneKey) { scene.current = sceneKey; for (const k of [...heroes.map((h) => `h:${h.key}`), ...heroes.flatMap((h) => (h.from ? [`n:${h.from}`] : [])), ...npcs.map((n) => `n:${n.key}`), 'helper']) mover.drop(k); }
  useEffect(() => { setPassers([]); }, [sceneKey]);
  const ensure = (k: string, st: Station, face?: 1 | -1): Spot => mover.at(k) ?? (mover.place(k, st, face ?? (st.flip ? -1 : 1), st.seated), mover.at(k)!);
  // my own heroes walk where I send them; the first of mine leads
  const walkers = mineH.map((h) => `h:${h.key}`);
  const tell = (pose: 0 | 1) => {
    const l = walkers[0] ? mover.at(walkers[0]) : undefined;
    if (l) onStandRef.current?.({ x: Math.round(l.x * 100) / 100, y: Math.round(l.y * 100) / 100, pose });
    // docs/116: walking off (to talk, to the table) ends whatever the band was doing in the inn
    const st = useInn.getState();
    if (st.pose !== pose) { st.setPose(pose); st.act(mineH.map((h) => h.key), undefined); st.setBook(undefined); }
  };
  const onStandRef = useRef(onStand);
  onStandRef.current = onStand;
  const leaderKey = walkers[0];
  const [walkingTo, setWalkingTo] = useState<string>();
  const stepKey: SfxKey = inside ? (STYLE_OF[spot as BuildingKind] === 'smithy' || STYLE_OF[spot as BuildingKind] === 'temple' ? 'step.stone' : 'step.wood') : street?.theme.snowy ? 'step.snow' : street?.theme.lane === 'sand' ? 'step.sand' : 'step.dirt';

  // ---- docs/120 VM2: tell the other views where my figures are --------------------------------------
  const share = useRef<{ t?: number; last: number }>({ last: 0 });
  const onFiguresRef = useRef(onFigures);
  onFiguresRef.current = onFigures;
  const mineRef = useRef(mineH);
  mineRef.current = mineH;
  const talkRef = useRef<string | undefined>(undefined);
  talkRef.current = talking ?? walkingTo;
  const sendFigures = () => {
    const sh = share.current;
    window.clearTimeout(sh.t); sh.t = undefined; sh.last = performance.now();
    const st = useInn.getState();
    const figs = mineRef.current.flatMap((h) => {
      const id = h.key.replace(/-beast$/, ''), p = mover.plan(`h:${h.key}`);
      return p && /^\d+$/.test(id) ? [toFigure(BigInt(id), !!h.companion, p, st.acts[h.key], st.mugs[h.key])] : [];
    });
    if (figs.length) onFiguresRef.current?.(figs, talkRef.current);
  };
  // a burst of changes (a band setting off one by one) is told at most four times a second
  const shareSoon = () => { const sh = share.current; if (sh.t !== undefined) return; sh.t = window.setTimeout(sendFigures, Math.max(60, 250 - (performance.now() - sh.last))); };
  useEffect(() => { mover.onChange((k) => { if (mineRef.current.some((h) => `h:${h.key}` === k)) shareSoon(); }); return () => mover.onChange(undefined); }, [mover]);
  useEffect(() => { shareSoon(); }, [sceneKey, walkers.join(), talking, walkingTo]);
  useEffect(() => useInn.subscribe((a, b) => { if (a.acts !== b.acts || a.mugs !== b.mugs) shareSoon(); }), []);
  // and now and then anyway (a reconnect forgets the row)
  useEffect(() => { const t = window.setInterval(() => { if (performance.now() - share.current.last > 9000) shareSoon(); }, 3000); return () => window.clearInterval(t); }, []);

  // ---- docs/120 VM4: nobody stands on anyone; one sitter a seat ---------------------------------------
  /** Where everyone but my band stands (or is walking to), and the seats and stations the others hold. */
  // `yielding`: only those I make way for (when two players stand on one spot, the one whose identity
  // sorts later steps aside, so exactly one of them moves, the same in every view)
  const othersAt = (yielding = false): Pt[] => [
    ...heroes.filter((h) => h.mine === false && (!yielding || (h.owner ?? '') < me)).map((h) => { const r = remoteFig.get(`h:${h.key}`); return r ? figureAt(r.f, 1e6).at : mover.at(`h:${h.key}`); }),
    ...here.map((n) => mover.at(`n:${n.key}`)),
    ...[...othersHeld].map((c) => seatMap.get(c)),
  ].filter((p): p is Pt => !!p);
  const othersRef = useRef(othersAt);
  othersRef.current = othersAt;
  /** The seats and stations my heroes hold now (codes). */
  const holding = useRef<number[]>([]);
  const sit = (codes: Array<{ seat: number; heroId: bigint }>): Promise<boolean> => {
    if (!codes.length && !holding.current.length) return Promise.resolve(true);
    holding.current = codes.map((c) => c.seat);
    return onSit ? onSit(codes) : Promise.resolve(true);
  };
  /** After a walk: any of mine standing on someone (or on one another) steps to a clear spot nearby. */
  const settle = () => window.setTimeout(() => {
    const taken = othersRef.current(true);
    for (const k of walkers) {
      const p = mover.at(k);
      if (!p || p.seated || mover.walking(k)) { if (p) taken.push(p); continue; }
      if (taken.some((t) => overlaps(t, p))) {
        const to = clearSpot(grid, p, taken, OVERLAP);
        if (Math.hypot(to.x - p.x, to.y - p.y) > 0.05) mover.walk(k, findPath(grid, p, to), { speed: WALK_SPEED * 0.6, end: { face: p.face } });
        taken.push(to);
      } else taken.push(p);
    }
  }, 350);
  const heroIdOf = (k: string) => { const id = k.slice(2).replace(/-beast$/, ''); return /^\d+$/.test(id) ? BigInt(id) : undefined; };

  /** Walk the band over to a villager in single file, then open the talk. */
  const approach = (npcKey: string) => {
    const nk = `n:${npcKey}`;
    const npc = mover.at(nk);
    const lead = leaderKey ? mover.at(leaderKey) : undefined;
    // at the table the band stays seated and the villager is called over in words
    if (mealOn || !npc || !lead || !leaderKey) { onTalk(npcKey); return; }
    mover.stop(nk);
    const turn = () => { const l = mover.at(leaderKey)!; mover.face(leaderKey, npc.x >= l.x ? 1 : -1); mover.face(nk, l.x <= npc.x ? -1 : 1); };
    // docs/120 VM4: beside them where nobody else stands (another band may be talking to them too)
    const to = clearSpot(grid, besideSpot(grid, npc, lead), othersAt().filter((p) => Math.hypot(p.x - npc.x, p.y - npc.y) > 0.05));
    void sit([]);
    if (Math.hypot(to.x - lead.x, to.y - lead.y) < 0.4) { turn(); onTalk(npcKey); return; }
    const f = fileAlong(findPath(grid, lead, to), walkers.slice(1).map((k) => mover.at(k)).filter((p): p is Spot => !!p));
    setWalkingTo(npcKey);
    walkers.forEach((k, i) => mover.walk(k, f.line, { from: f.from[i] ?? 0, to: f.to[i] ?? 0, speed: WALK_SPEED, ...(i === 0 ? { end: { face: npc.x >= to.x ? 1 : -1 }, done: () => { turn(); setWalkingTo(undefined); onTalk(npcKey); tell(0); settle(); } } : {}) }));
  };   // (the villager's own button clicks; a summon walks over in silence)
  useEffect(() => { if (summon) approach(summon.key); }, [summon?.n]);

  /** Walk a figure to a station, up off a stool first and down onto one at the end (docs/73). */
  const goTo = (k: string, st: Station, speed: number, done?: () => void): boolean => {
    const cur = mover.at(k);
    if (!cur) return false;
    const off = cur.seated ? { x: cur.x, y: cur.y - 0.25 } : cur;
    if (cur.seated) mover.place(k, off, cur.face);
    const path = findPath(grid, off, st.seated ? { x: st.x, y: st.y - 0.25 } : st);
    const face = st.flip === undefined ? undefined : st.flip ? -1 : 1;
    mover.walk(k, st.seated ? [...path, { x: st.x, y: st.y }] : path, { speed, end: { ...(face ? { face } : {}), ...(st.seated ? { seated: true } : {}) }, done: () => { mover.place(k, st, face ?? mover.at(k)?.face, st.seated); done?.(); } });
    return true;
  };
  // docs/116 CI3: my band walks to the inn's stations for a pose, each figure to its own (the leader to the
  // first), and the other players are told once the leader is there
  // Only the latest walk arrives; one cut short (a figure re-walked, or not placed yet) still arrives
  // when the guard runs out, so a place's panel always opens.
  // docs/120 VM4: a seat or station (an armchair, a place at the hearth, a bed) is held for the figure sent
  // to it; one someone else holds is never taken. If another player got there first the walk is
  // planned again (`again`) with what they now hold. Places on the floor are kept clear of everyone.
  const bandWalk = useRef(0);
  const retries = useRef(0);
  const walkBand = (sts: Station[], pose: PoseCode, done?: () => void, again?: () => void) => {
    const n = ++bandWalk.current;
    let left = walkers.length;
    const arrive = () => { if (bandWalk.current !== n) return; bandWalk.current++; window.clearTimeout(guard); tellPose(pose); done?.(); settle(); };
    const one = () => { if (--left === 0) arrive(); };
    const guard = window.setTimeout(arrive, 9000 + walkers.length * 140);
    if (!left) { arrive(); return; }
    const taken = othersAt();
    const plan = walkers.map((k, i) => {
      const want: Station = sts[i] ?? nearestFree(grid, { x: (sts[0]?.x ?? room?.band.x ?? 2) + (i % 2 ? 0.8 : -0.8) * Math.ceil(i / 2), y: (sts[0]?.y ?? grid.y0) + 0.5 });
      const code = codeOf(seatMap, want);
      const st: Station = code !== undefined || want.seated ? want : { ...clearSpot(grid, want, taken), ...(want.flip !== undefined ? { flip: want.flip } : {}) };
      taken.push(st);
      return { k, st, code };
    });
    const codes = plan.flatMap((p) => { const id = heroIdOf(p.k); return p.code !== undefined && id !== undefined ? [{ seat: p.code, heroId: id }] : []; });
    void sit(codes).then((ok) => {
      if (ok) { retries.current = 0; return; }
      holding.current = [];
      if (bandWalk.current !== n) return;
      onNote?.('Someone got there first.');
      if (retries.current++ < 2) window.setTimeout(() => again?.(), 300);
    });
    plan.forEach(({ k, st }, i) => {
      window.setTimeout(() => { if (bandWalk.current === n && !goTo(k, st, WALK_SPEED * 0.85, one)) one(); }, i * 140);
    });
    // (no click here: the inn's spot buttons click themselves, and the band also walks when nobody clicked)
  };
  const tellPose = (pose: PoseCode) => { const l = walkers[0] ? mover.at(walkers[0]) : undefined; if (l) onStandRef.current?.({ x: Math.round(l.x * 100) / 100, y: Math.round(l.y * 100) / 100, pose }); };
  // docs/73 TP4: a villager whose place changes (a band wants their stool, or has left the table) walks there
  const placesSig = [...places.entries()].map(([k, p]) => `${k}:${p.x.toFixed(2)},${p.y.toFixed(2)}`).join();
  useEffect(() => {
    if (!room) return;
    for (const n of here) {
      const k = `n:${n.key}`, st = places.get(n.key), cur = mover.at(k);
      if (!st || !cur || n.key === talking || n.key === walkingTo || mover.walking(k)) continue;
      if (Math.abs(cur.x - st.x) < 0.05 && Math.abs(cur.y - st.y) < 0.05) continue;
      goTo(k, st, 1.6);
    }
  }, [placesSig]);

  // docs/120 VM2: every other player's figures go where their row says, mid-stride if they are walking
  const replayed = useRef(new Map<string, string>());
  const heroKeysSig = heroes.map((h) => h.key).join();
  useEffect(() => {
    const seen = replayed.current;
    for (const r of remote ?? []) {
      const sig = `${r.atMs}:${sceneKey}:${heroKeysSig}`;
      if (seen.get(r.owner) === sig) continue;
      seen.set(r.owner, sig);
      const secs = (smoothServerNow() - r.atMs) / 1000;
      for (const f of r.figures) { const k = figureKey(f.heroId, f.beast); if (mover.at(k)) replay(mover, k, f, secs, WALK_SPEED); }
    }
  }, [remoteSig, sceneKey, heroKeysSig]);
  // docs/120 VM4: someone came to stand where one of mine stands (or we walked in together): make way
  useEffect(() => { const t = settle(); return () => window.clearTimeout(t); }, [remoteSig, sceneKey]);
  // what another player's heroes are doing in the inn (a mug, a book, asleep), from their row
  const remoteActs = useMemo(() => new Map([...remoteFig.entries()].map(([k, r]) => [k.slice(2), { act: actOf(r.f.act), mug: heldOf(r.f.held) }])), [remoteFig]);
  // docs/73 TP2: a player whose view has told nothing of its figures yet (an older client) stands where its
  // band's leader last walked to
  const crowdSig = crowd.map((c) => `${c.owner}:${c.heroes.map((h) => h.key).join('.')}:${c.stand?.x},${c.stand?.y}`).join('|');
  useEffect(() => {
    for (const c of crowd) {
      if (c.heroes.some((h) => remoteFig.has(`h:${h.key}`))) continue;
      const keys = c.heroes.map((h) => `h:${h.key}`).filter((k) => mover.at(k));
      if (!keys.length || !c.stand || c.stand.x < 0) continue;
      const lead = mover.at(keys[0]!)!;
      const to = nearestFree(grid, c.stand);
      if (Math.hypot(lead.x - to.x, lead.y - to.y) < 0.3) continue;
      const f = fileAlong(findPath(grid, lead, to), keys.slice(1).map((k) => mover.at(k)!));
      keys.forEach((k, i) => mover.walk(k, f.line, { from: f.from[i] ?? 0, to: f.to[i] ?? 0, speed: WALK_SPEED }));
    }
  }, [crowdSig, sceneKey]);

  // docs/73 TP3: a villager just taken on steps out of where they stood and falls in at the back of the file
  const seenMine = useRef<{ scene: string; keys: Set<string> }>({ scene: '', keys: new Set() });
  useEffect(() => {
    const was = seenMine.current;
    seenMine.current = { scene: sceneKey, keys: new Set(walkers) };
    if (was.scene !== sceneKey) return;
    for (const h of mineH) {
      const k = `h:${h.key}`;
      if (!h.from || was.keys.has(k)) continue;
      const line = walkers.filter((x) => x !== k).map((x) => mover.at(x)).filter((p): p is Spot => !!p);
      const lead = line[0], last = line.at(-1);
      if (!lead || !last) continue;
      const to = nearestFree(grid, { x: last.x - lead.face * FILE_GAP, y: last.y });
      mover.walk(k, findPath(grid, mover.at(k) ?? to, to), { speed: WALK_SPEED * 0.8, done: () => mover.face(k, lead.face) });
    }
  }, [walkers.join(), sceneKey]);

  // what the frame loop reads (the latest render's view of things)
  const placesRef = useRef(places);
  placesRef.current = places;
  // docs/120: the villagers other players stand talking to stay put in my view too
  const heard = useMemo(() => new Set((remote ?? []).flatMap((r) => (r.talkNpc ? [r.talkNpc] : []))), [remote]);
  // the hens' homes on the verge (none some days, none after dark)
  const hens = useMemo<Pt[]>(() => (!street || night ? [] : [0, 1].slice(0, hashN(look.seed, 41) < 0.35 ? 0 : hashN(look.seed, 42) < 0.5 ? 1 : 2)
    .map((h) => nearestFree(grid, { x: edge + (street.square.x - edge + 1 + h * 1.3 + hashN(look.seed, h, 43) * 6) % Math.max(8, cols - 2), y: 6.3 }))), [street, night, look.seed, grid, edge, cols]);
  const live = useRef({ here: [] as VillageNpc[], places, grid, talking, walkingTo, street, stepKey, walkers, motion: true, night, w: gw, heard, seed: look.seed, hens });
  live.current = { here: npcs.filter((n) => n.at === spot), places, grid, talking, walkingTo, street, stepKey, walkers, motion: !reduced, night, w: gw, heard, seed: look.seed, hens };
  useEffect(() => {
    let stepIn = 0, lastLook = -1, plansSig = '';
    const plans = new Map<number, Passer | undefined>();
    const ambled = new Map<string, number>();
    mover.onFrame((dt, moving) => {
      const L = live.current;
      // footsteps under the band while it walks
      if (L.walkers.some((k) => moving.includes(k))) { stepIn -= dt; if (stepIn <= 0) { stepIn = 0.36; playSfx(L.stepKey, { gain: 0.4 }); } } else stepIn = 0;
      if (!L.motion || !L.street) return;
      // villagers amble a few steps up and down the lane, never while spoken to or walked to (by anyone).
      // docs/120 VM2: where each goes and when is a function of the World's clock, so every view sees
      // them at the same place
      const now = smoothServerNow() / 1000;
      for (const n of L.here) {
        const k = `n:${n.key}`;
        if (n.key === L.talking || n.key === L.walkingTo || L.heard.has(n.key)) { mover.stop(k); continue; }
        const home = L.places.get(n.key), cur = mover.at(k);
        if (!home || !cur || ambled.get(k) === ambleStep(L.seed, n.key, now).n) continue;
        const a = ambleOf(L.seed, n.key, home, L.grid, now, AMBLE_SPEED);
        const first = !ambled.has(k);
        ambled.set(k, a.n);
        const path = findPath(L.grid, first ? a.from : cur, a.to);
        const from = first ? Math.min(lengthOf(path), a.into * a.speed) : 0;
        if (first) mover.place(k, pointAt(path, from), cur.face);
        mover.walk(k, path, { from, speed: a.speed, end: { face: a.face } });
      }
      // docs/121 VW1: passers-by and the hens are the same in every view: who passes and when is the
      // village's seed and the World's clock (`scenery.ts`), so each view puts them where the others do
      if (now - lastLook >= 0.25) {
        lastLook = now;
        const shape: StreetShape = { w: L.w, doors: L.street.doors.map((d) => d.door), night: L.night, grid: L.grid };
        // a new seed or street (the World's row arriving after the first frame, nightfall) is a new set of passers
        const sig = `${L.seed}:${L.w}:${L.night ? 1 : 0}:${shape.doors.join(',')}`;
        if (sig !== plansSig) {
          plansSig = sig;
          plans.clear();
          for (const k of mover.keys()) if (k.startsWith('w:')) mover.drop(k);
          setPassers([]);
        }
        const ks = passerRounds(now);
        for (const k of [...plans.keys()]) if (k < ks[0]!) plans.delete(k);
        for (const k of ks) {
          if (!plans.has(k)) plans.set(k, passerOf(L.seed, k, shape));
          const p = plans.get(k);
          const key = `w:${k}`;
          if (!p) continue;
          const along = passerAlong(p, now);
          const cur = mover.at(key);
          if (cur) {
            // a busy frame loop falls behind the clock: gone by the clock is gone here too, and a passer
            // more than a little off where the clock has them is put back on their walk
            if (along === undefined) { mover.drop(key); setPassers((ps) => ps.filter((q) => q.k !== k)); continue; }
            const want = pointAt(p.line, along);
            if (Math.hypot(want.x - cur.x, want.y - cur.y) < 0.6) continue;
            mover.place(key, want, cur.face);
            mover.walk(key, p.line, { from: along, speed: p.pace, done: () => { mover.drop(key); setPassers((ps) => ps.filter((q) => q.k !== k)); } });
            continue;
          }
          if (along === undefined) continue;
          const at = pointAt(p.line, along), end = p.line[p.line.length - 1]!;
          mover.place(key, at, end.x >= at.x ? 1 : -1);
          setPassers((ps) => [...ps.filter((q) => q.k !== k), p]);
          const fresh = now - p.start < 1;
          if (fresh && p.fromDoor) playSfx('mech.door', { band: DOOR_WOOD, gain: 0.3 });
          if (fresh && p.call) playSfx(p.kind === 'dog' ? 'cue.dog_bark' : 'pet.cat_happy', { gain: 0.3, delayMs: 600 + hashN(L.seed, k, 3) * 1500 });
          mover.walk(key, p.line, { from: along, speed: p.pace, done: () => { mover.drop(key); setPassers((ps) => ps.filter((q) => q.k !== k)); if (p.intoDoor) playSfx('mech.door', { band: DOOR_WOOD, gain: 0.25 }); } });
        }
      }
      // the hens peck about where they live, a step at a time on the same clock
      L.hens.forEach((home, h) => {
        const k = `hen:${h}`;
        const cur = mover.at(k);
        if (!cur || ambled.get(k) === wanderStep(L.seed, k, now, PECK).n) return;
        const a = wanderOf(L.seed, k, home, L.grid, now, PECK);
        const first = !ambled.has(k);
        ambled.set(k, a.n);
        const path = findPath(L.grid, first ? a.from : cur, a.to);
        const from = first ? Math.min(lengthOf(path), a.into * a.speed) : 0;
        if (first) mover.place(k, pointAt(path, from), cur.face);
        mover.walk(k, path, { from, speed: a.speed, end: { face: a.face } });
      });
    });
    return () => mover.onFrame(undefined);
  }, [mover]);

  // ---- docs/64 VP4: a meal at the inn's long table -------------------------------------------------
  const [dishes, setDishes] = useState<Array<{ key: string; x: number; y: number; kind: DishKind; left: 0 | 1 | 2 }>>([]);
  const [helper, setHelper] = useState(false);
  const [resting, setResting] = useState(false);
  const [eating, setEating] = useState(false);
  const onMealRef = useRef(onMeal);
  onMealRef.current = onMeal;
  useEffect(() => {
    const t = room?.table;
    if (!meal || !t) return;
    let on = true;
    const timers: number[] = [];
    const later = (ms: number, fn: () => void) => { timers.push(window.setTimeout(() => { if (on) fn(); }, ms)); };
    const stage = (s: MealStage) => onMealRef.current?.(s);
    // the heroes sit along the table, the leader at the far end; a ranger's beast lies at its near end with a bowl
    const beastKeys = new Set(heroes.filter((h) => h.companion).map((h) => `h:${h.key}`));
    const walking = meal.diners.map((k) => `h:${k}`).filter((k) => mover.at(k));
    const diners = [...walking.filter((k) => !beastKeys.has(k)), ...walking.filter((k) => beastKeys.has(k))];
    // docs/73 TP4, docs/120 VM4: my band takes seats nobody else holds (a villager's stool only when it must,
    // and the villager gets up for it), and holds them; a beast, or a diner the tables cannot take, stands aside
    const people = diners.filter((k) => !beastKeys.has(k)).length;
    const held = heldRef.current;
    const freeSeats = tSeats.filter((_, i) => !held.has(SEAT.table + i));
    const stools = here.flatMap((n) => { const st = usualRef.current.get(n.key); return st?.seated ? [{ key: n.key, x: st.x, y: st.y }] : []; });
    const mySeats: Seat[] = seatPlan(freeSeats, stools, [{ key: 'me', n: people }]).seats.get('me') ?? [];
    const seats = Math.min(people, mySeats.length);
    const idx = mySeats.slice(0, seats).map((st) => tSeats.indexOf(st));
    setMyTable(idx);
    void sit(diners.slice(0, seats).flatMap((k, i) => { const id = heroIdOf(k); return id !== undefined ? [{ seat: SEAT.table + idx[i]!, heroId: id }] : []; }));
    const taken: Pt[] = [...here.map((n) => placesRef.current.get(n.key)).filter((p): p is Station => !!p), ...mySeats, ...[...held].map((c) => seatMap.get(c)).filter((p): p is Station => !!p),
      ...othersAt()];
    const aside = asideSpots(grid, taken, { x: t.x - 1, y: grid.y0 }, diners.length - seats);
    const seat = (i: number): Pt => (i < seats ? mySeats[i]! : aside[i - seats]!);
    stage('seating');
    // the band files in behind the table, the leader to the far end, and each sits at their place
    let seated = 0;
    const sitDown = (k: string, i: number) => mover.walk(k, [mover.at(k)!, seat(i)], { speed: 2, end: { face: i >= seats ? 1 : i % 2 ? 1 : -1, ...(i < seats ? { seated: true } : {}) }, done: () => {
      mover.place(k, seat(i), i >= seats ? 1 : i % 2 ? 1 : -1, i < seats);
      if (++seated === diners.length) { tell(1); serve(); }
    } });
    const lead = diners[0] ? mover.at(diners[0]) : undefined;
    if (!lead) return;
    const f = fileAlong(findPath(grid, lead, { x: seat(0).x, y: t.y - 1 }), diners.slice(1).map((k) => mover.at(k)!));
    diners.forEach((k, i) => mover.walk(k, f.line, { from: f.from[i] ?? 0, to: f.to[i] ?? 0, speed: WALK_SPEED * 0.8, done: () => sitDown(k, i) }));
    // (the meal's coins already rang when it was paid for: Village.tsx `pay`)
    // the serving hand comes out of the kitchen with the tray, sets the table and goes back
    const serve = () => {
      stage('serving');
      const door: Pt = { x: room!.w - 2, y: t.y - 0.9 };
      mover.place('helper', door, -1);
      setHelper(true);
      const by: Pt = nearestFree(grid, { x: t.x + t.w + 0.4, y: t.y - 1 });
      mover.walk('helper', findPath(grid, door, by), { speed: 2.2, done: () => {
        mover.face('helper', -1);
        diners.forEach((k, i) => later(250 * i, () => {
          const s = seat(i);
          setDishes((d) => [...d, ...(i >= seats && !beastKeys.has(k) ? [{ key: `${k}:mug`, x: s.x + 0.5, y: s.y + 0.35, kind: 'mug' as const, left: 0 as const }]
            : i >= seats ? [{ key: `${k}:bowl`, x: s.x + 0.45, y: s.y + 0.72, kind: 'stew' as const, left: 0 as const }]
            : [{ key: `${k}:food`, x: s.x + 0.05, y: t.y, kind: (i % 2 ? 'bread' : 'stew') as DishKind, left: 0 as const }, { key: `${k}:mug`, x: s.x + 0.5, y: t.y, kind: 'mug' as const, left: 0 as const }])]);
          // a dish set down, and every other place a drink poured into its mug
          playSfx('inn.mug', { gain: 0.5 });
          if (i % 2 === 0) playSfx('inn.pour', { gain: 0.45, delayMs: 120 });
        }));
        later(250 * diners.length + 500, () => {
          mover.walk('helper', findPath(grid, by, door), { speed: 2.2, done: () => { setHelper(false); mover.drop('helper'); } });
          eat();
        });
      } });
    };
    // they eat: the plates empty, a bite and a swig at a time
    const eat = () => {
      stage('eating');
      setEating(true);
      // a bite at a time (`item.eat`: short bites, not the tavern's whole minute-long bed)
      playSfx('item.eat', { gain: 0.6 });
      later(2600, () => { setDishes((d) => d.map((x) => ({ ...x, left: 1 }))); playSfx('item.eat', { gain: 0.5 }); });
      later(5600, () => { setDishes((d) => d.map((x) => ({ ...x, left: 2 }))); playSfx('item.eat', { gain: 0.45 }); });
      later(6800, () => { setEating(false); stage('resting'); setResting(true); });
    };
    // then they sit back and talk until the player moves them on (docs/121 VW3: `tableTalkAt`, the same in every view)
    return () => {
      on = false;
      for (const x of timers) window.clearTimeout(x);
      setDishes([]); setHelper(false); setResting(false); setEating(false); mover.drop('helper');
      // up from the table, a step back onto the floor
      for (const k of diners) { const p = mover.at(k); if (p?.seated) mover.place(k, { x: p.x, y: t.y - 1 }, p.face); }
      // the seats are free again
      setMyTable([]);
      void sit([]);
      tell(0);
    };
  }, [meal?.n, room?.table?.x]);

  // ---- docs/121 VW3: every band's meal at the table, in every view -----------------------------------
  // Another player's band seated at the inn's tables with a meal ordered is served, eats and talks in my
  // view on the meal's own clock (when it was ordered); what my band says at the table is the same
  // function, so their views hear mine too.
  const mealOf = useMemo(() => new Map((meals ?? []).map((m) => [m.heroKey, m.atMs])), [meals]);
  const [clockS, setClockS] = useState(() => smoothServerNow() / 1000);
  const remoteDiners = !room?.table ? [] : heroes.flatMap((h) => {
    if (h.mine !== false || h.companion) return [];
    const r = remoteFig.get(`h:${h.key}`), atMs = mealOf.get(h.key);
    if (!r?.f.seated || atMs === undefined || clockS - atMs / 1000 > MEAL_SHOWN_S) return [];
    const p = figureAt(r.f, 1e6).at;
    const seat = tSeats.find((t) => Math.abs(t.x - p.x) < 0.1 && Math.abs(t.y - p.y) < 0.1);
    return seat ? [{ key: h.key, owner: h.owner ?? '', seat, atMs }] : [];
  });
  const myMealAt = meal ? Math.max(0, ...meal.diners.map((k) => mealOf.get(k) ?? 0)) : 0;
  const needClock = remoteDiners.length > 0 || (mealOn && resting);
  useEffect(() => { if (!needClock) return; const t = window.setInterval(() => setClockS(smoothServerNow() / 1000), 400); return () => window.clearInterval(t); }, [needClock]);
  const talk = new Map<string, { text: string; n: number }>();
  const groups = new Map<string, { atMs: number; keys: string[] }>();
  for (const d of remoteDiners) { const g = groups.get(d.owner) ?? { atMs: d.atMs, keys: [] }; g.atMs = Math.max(g.atMs, d.atMs); g.keys.push(d.key); groups.set(d.owner, g); }
  if (mealOn && resting && meal) groups.set(me, { atMs: myMealAt || look.seed + meal.n, keys: meal.diners.filter((k) => !heroes.find((h) => h.key === k)?.companion) });
  for (const [owner, g] of groups) {
    if (owner !== me && !mealAt(clockS - g.atMs / 1000).resting) continue;
    const t = tableTalkAt(Math.floor(g.atMs) >>> 0, [...g.keys].sort(), clockS);
    if (t) talk.set(t.key, { text: t.text, n: t.n });
  }
  const lastLaugh = useRef(-1);
  useEffect(() => {
    if (!mealOn || !resting || !meal) return;
    const t = tableTalkAt(Math.floor(myMealAt || look.seed + meal.n) >>> 0, [...meal.diners].sort(), clockS);
    if (t?.laugh && lastLaugh.current !== t.n) { lastLaugh.current = t.n; playSfx('emote.laughing', { gain: 0.35 }); }
  }, [clockS]);
  const remoteDishes = remoteDiners.flatMap((d, i) => {
    const m = mealAt(clockS - d.atMs / 1000), t = room?.tables?.[d.seat.table] ?? room?.table;
    if (!m.served || !t) return [];
    return [{ key: `r:${d.key}:food`, x: d.seat.x + 0.05, y: t.y, kind: (hashN(d.atMs, i) < 0.5 ? 'bread' : 'stew') as DishKind, left: m.left }, { key: `r:${d.key}:mug`, x: d.seat.x + 0.5, y: t.y, kind: 'mug' as const, left: m.left }];
  });
  const remoteEating = new Set(remoteDiners.filter((d) => mealAt(clockS - d.atMs / 1000).eating).map((d) => d.key));
  // docs/121 VW10: the serving hand who brings another band its meal, in my view too, on the meal's clock
  const serves = new Map<string, { atMs: number; table: number }>();
  for (const d of remoteDiners) { const v = serves.get(d.owner); if (!v || d.atMs > v.atMs) serves.set(d.owner, { atMs: d.atMs, table: d.seat.table }); }
  const remoteHands = !room ? [] : [...serves].flatMap(([owner, v]) => {
    const t = room.tables?.[v.table] ?? room.table;
    if (!t) return [];
    const door: Pt = { x: room.w - 2, y: t.y - 0.9 }, by = nearestFree(grid, { x: t.x + t.w + 0.4, y: t.y - 1 });
    const h = serveAt(grid, door, by, clockS - v.atMs / 1000);
    return h ? [{ key: `helper:${owner}`, ...h }] : [];
  });
  const handLegs = remoteHands.map((h) => `${h.key}/${h.leg}`).join(' ');
  useEffect(() => {
    // each leg is walked from where its schedule puts the hand now (a view coming in mid-walk picks it up there)
    for (const h of remoteHands) {
      mover.place(h.key, h.at, h.leg === 'out' ? -1 : 1);
      if (!h.done) mover.walk(h.key, h.line, { from: h.along, speed: SERVE_PACE, ...(h.leg === 'out' ? { end: { face: -1 } } : {}) });
    }
    const keys = remoteHands.map((h) => h.key);
    return () => { for (const k of keys) mover.drop(k); };
  }, [handLegs]);

  const lampGlows = (street?.lamps ?? room?.lamps ?? []).filter((l) => night || l.kind === 'fire' || inside);
  // docs/116: the inn's layer and panels (the inn and the room upstairs)
  const innActs = useInn((s) => s.acts);
  const innMugs = useInn((s) => s.mugs);
  const innBook = useInn((s) => s.book);
  useEffect(() => { if (spot !== 'inn' && spot !== 'upstairs') useInn.getState().reset(); else useInn.getState().setPose(POSE.standing); }, [spot]);
  const innStage: InnStage | undefined = room?.inn && inn && (spot === 'inn' || spot === 'upstairs') ? {
    room, plan: room.inn, upstairs: spot === 'upstairs', look, mover, grid, cellPx, scale, phase, ...(sheets ? { sheets } : {}), reduced, heroes, mine: mineH, data: inn,
    acts: Object.fromEntries(heroes.map((h) => [h.key, h.mine !== false ? innActs[h.key] : remoteActs.get(h.key)?.act])),
    folk: here.map((n) => n.key), walkBand,
    occupied: (except = []) => [...mover.keys().filter((k) => !except.includes(k) && !k.startsWith('w:')).map((k) => mover.at(k)!).filter(Boolean), ...othersAt()],
    free: (st) => !othersAt().some((p) => overlaps(p, st)),
    ...(onNote ? { note: onNote } : {}),
    at: (x, y) => ({ left: Math.round(x * cellPx), top: Math.round(y * cellPx) }),
  } : undefined;
  const smoke = street ? street.buildings.map(chimneyTop).filter((p): p is { x: number; y: number } => !!p) : [];

  // docs/121 VW1: the sky's movers (birds, the bat, the clouds) are CSS loops started on the World's
  // clock, so each is at the same place in every view
  const skyT = useMemo(() => smoothServerNow() / 1000, [sceneKey]);
  const drift = (dur: number): CSSProperties => ({ animationDelay: `${-((skyT % dur)).toFixed(2)}s` });
  const cls = ['fk-village', `fk-village--${spot}`, inside ? 'fk-village--inside' : 'fk-village--outside', night ? 'fk-village--night' : 'fk-village--day', weather ? `fk-village--sky-${weather}` : '', clouded ? 'fk-village--clouded' : '', `fk-village--${biome}`, season ? `fk-village--${season}` : '', reduced ? 'fk-village--still' : ''].filter(Boolean).join(' ');
  return (
    <div ref={rootRef} className={cls} data-testid="village-view" data-spot={spot} data-ready={ready ? 'true' : 'false'} data-night={night ? 'true' : 'false'} data-scale={scale}
      style={{ ['--cell' as string]: `${cellPx}px`, ['--s' as string]: scale, ['--horizon' as string]: `${(gh - BASE) * cellPx}px` } as CSSProperties}>
      {!inside && (
        <div className="fk-village__sky" aria-hidden="true">
          {stars.map((s, i) => <span key={i} className={`fk-scene__star ${s.tw ? 'fk-scene__star--tw' : ''}`} style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }} />)}
          {night && !clouded && <span className="fk-scene__moon fk-village__moon" />}
          {!night && !clouded && <><span className="fk-village__sun" /><span className="fk-village__rays" /></>}
          {(!night || clouded) && <div className="fk-village__cloudband" style={drift(240)}><Clouds seed={look.seed} scale={scale} dark={night || clouded} /></div>}
          {!night && !clouded && <div className="fk-village__cloudband fk-village__cloudband--far" style={drift(420)}><Clouds seed={look.seed + 11} scale={1} dark={false} /></div>}
          {clouded && <div className="fk-village__cloudband fk-village__cloudband--low" style={drift(150)}><Clouds seed={look.seed + 5} scale={scale} dark /></div>}
          {motion && !night && !['storm', 'blizzard', 'sandstorm'].includes(weather ?? '') && [0, 1, 2].map((i) => (
            <span key={i} className="fk-village__bird" style={{ top: `${14 + i * 7 + hashN(look.seed, i) * 10}%`, animationDelay: `${-((skyT + i * 6 + hashN(look.seed, i, 2) * 10) % (22 + i * 5)).toFixed(2)}s`, animationDuration: `${22 + i * 5}s` }}>
              <Sprite sheet={sheets?.creatures} frame={(i === 1 ? 234 : 232) + (phase === (i % 2) ? 18 : 0)} scale={scale === 3 ? 2 : 1} />
            </span>
          ))}
          {motion && night && <span className="fk-village__bat" style={drift(BAT_S)}><Sprite sheet={sheets?.creatures} frame={219 + (phase ? 18 : 0)} scale={scale === 3 ? 2 : 1} /></span>}
        </div>
      )}
      {inside && <div className="fk-village__room-bg" aria-hidden="true" />}

      <div ref={stageRef} className="fk-village__stage" data-testid="village-stage" style={{ width: stageW, height: stageH, marginLeft: -Math.round(stageW / 2) }}>
        {street && <canvas ref={farRef} className="fk-village__canvas fk-village__canvas--far" width={gw * CELL} height={(FAR_HEAD + BASE + 1) * CELL} style={{ top: -FAR_HEAD * cellPx, width: stageW, height: (FAR_HEAD + BASE + 1) * cellPx }} aria-hidden="true" />}
        <canvas ref={baseRef} className="fk-village__canvas" width={gw * CELL} height={gh * CELL} style={{ width: stageW, height: stageH }} aria-hidden="true" />
        {/* light: lantern pools on the lane, the hearth's flicker, the forge's glow */}
        {lampGlows.map((l, i) => <span key={i} className={`fk-village__light fk-village__light--${l.kind}`} style={{ ...at(l.x, l.y), animationDelay: `${-(i * 0.37) % 1.3}s` }} aria-hidden="true" />)}
        {street && night && street.buildings.flatMap((b) => b.windows.filter((x) => b.style !== 'temple' && windowLit(look.seed, b, x)).map((x) => <span key={`${b.x}-${x}`} className="fk-village__spill" style={at(x + 0.5, BASE + 1.15)} aria-hidden="true" />))}
        {street && motion && smoke.map((p, i) => (
          <span key={i} className={`fk-village__smoke${street.buildings.find((b) => chimneyTop(b)?.x === p.x)?.style === 'smithy' ? ' fk-village__smoke--forge' : ''}`} style={{ left: p.x * scale, top: p.y * scale }} aria-hidden="true">
            {[0, 1, 2, 3].map((k) => <span key={k} style={{ animationDelay: `${-k * 1.6 - i * 0.7}s` }} />)}
          </span>
        ))}
        {fireflies && motion && <div className="fk-village__fireflies" aria-hidden="true">{Array.from({ length: 16 }, (_, i) => <span key={i} style={{ left: `${hashN(look.seed, i, 5) * 100}%`, top: `${45 + hashN(look.seed, i, 6) * 40}%`, animationDelay: `${-hashN(look.seed, i, 7) * 6}s` }} />)}</div>}
        {spot !== 'street' && STYLE_OF[spot] === 'inn' && room?.hearth && motion && <div className="fk-village__motes" aria-hidden="true" style={at(room!.hearth!.x + 1.5, room!.floor - 1)}>{Array.from({ length: 14 }, (_, i) => <span key={i} style={{ left: `${hashN(i, 3) * 100}%`, animationDelay: `${-hashN(i, 4) * 9}s` }} />)}</div>}
        {spot === 'smithy' && motion && <div className="fk-scene__embers fk-village__sparks" aria-hidden="true" style={at(room!.hearth!.x + 1, room!.floor)}>{Array.from({ length: 18 }, (_, i) => <span key={i} className="fk-scene__ember" style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i * 0.41) % 5}s`, animationDuration: `${2.6 + (i % 4) * 0.6}s` }} />)}</div>}
        {spot !== 'street' && ['temple', 'shop', 'inn'].includes(STYLE_OF[spot]) && room && room.windows.map((w, i) => (
          <span key={i} className={`fk-village__shaft fk-village__shaft--${spot}${night ? ' fk-village__shaft--night' : ''}`} style={{ ...at(w.x, w.y), height: (room.floor - w.y + 3) * cellPx }} aria-hidden="true" />
        ))}

        {/* the doors: each building's facade is its door */}
        {street?.doors.map((d) => {
          const b = street.buildings.find((q) => q.x === d.x)!;
          const f = facadeBox(b);
          return (
            <button key={d.kind} type="button" className="fk-hit fk-village__door" data-sfx="none" data-testid="village-door" data-building={d.kind}
              style={{ left: d.x * cellPx, top: f.top * scale, width: d.w * cellPx, height: (f.bottom - f.top) * scale }}
              aria-label={`Enter ${d.name}`} onClick={() => { playSfx('mech.door', { band: DOOR_WOOD }); onEnter(d.kind); }}>
              <span className="fk-village__doorway" style={{ left: (d.door - d.x) * cellPx, width: cellPx, height: cellPx }} />
              <span className="fk-village__plate fk-village__plate--door" style={{ left: (d.door - d.x + 0.5) * cellPx, top: Math.max(0, (f.wallTop - f.top) * scale - 8) }}>
                <span className="fk-village__plate-name">{d.name}</span><span className="fk-village__plate-sub">{INSIDE_NAME[d.kind]}</span>
              </span>
            </button>
          );
        })}
        {inside && (
          <button type="button" className="fk-hit fk-village__leave" data-sfx="none" data-testid="village-leave" style={{ ...at(room!.exit, room!.floor - 1), width: cellPx, height: cellPx }}
            aria-label={spot === 'upstairs' ? 'Downstairs' : 'Leave'} onClick={() => { if (spot === 'upstairs') { playSfx('inn.stairs'); onEnter('inn'); return; } playSfx('mech.door', { band: DOOR_WOOD }); onLeave(); }}>
            <span className="fk-village__plate fk-village__plate--leave"><span className="fk-village__plate-name">{spot === 'upstairs' ? 'Downstairs' : 'Leave'}</span><span className="fk-village__plate-sub">{spot === 'upstairs' ? 'back down to the inn' : 'back to the street'}</span></span>
          </button>
        )}

        {/* the band: where it came in, then wherever it walked (docs/64 VP2) */}
        {heroes.map((h, i) => {
          const k = `h:${h.key}`;
          const was = h.from ? mover.at(`n:${h.from}`) : undefined;
          const p = ensure(k, was ? { x: was.x, y: was.y } : heroStations[i]!, was?.face ?? 1);
          const frame = h.frame > 0 ? h.frame : CLASS_FRAMES[h.classId] ?? 1;
          // docs/111 TB1: a hero (not a beast) opens their stats card
          const id = h.companion ? undefined : h.id;
          const open = onHero && id !== undefined ? () => onHero(id) : undefined;
          return (
            <span key={h.key} ref={mover.reg(k)} className={`fk-village__figure fk-village__hero${h.companion ? ' fk-village__hero--beast' : ''}${(eating && meal?.diners.includes(h.key)) || remoteEating.has(h.key) ? ' fk-village__hero--eating' : ''}${open ? ' fk-hit fk-village__hero--open' : ''}`}
              data-testid="village-hero" data-hero={h.key} data-leader={k === leaderKey ? 'true' : undefined} data-face={p.face < 0 ? 'left' : 'right'} data-seated={p.seated ? 'true' : undefined} data-act={innStage ? (h.mine !== false ? innActs[h.key] : remoteActs.get(h.key)?.act) : undefined} title={open ? `${h.name}: see their stats card` : h.name}
              {...(open ? { role: 'button', tabIndex: 0, 'aria-label': `${h.name}: see their stats card`, onClick: open, onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } } } : {})}
              style={{ ...at(p.x, p.y), zIndex: Math.round(p.y * 10) + 1 }}>
              <span className="fk-village__shadow" />
              <span className="fk-village__face">{h.petId !== undefined
                // docs/62: a beast's pet emotes wiggle it, leaning forward to the ranger it follows
                ? <PetWiggle pet={{ id: h.petId, name: h.name, mine: !!h.mine }} lean={1}><Sprite sheet={sheets?.creatures} frame={offBeat(frame, phase === i % 2)} scale={scale} className="fk-village__body" /></PetWiggle>
                : h.companion ? <Sprite sheet={sheets?.creatures} frame={offBeat(frame, phase === i % 2)} scale={scale} className="fk-village__body" />
                  // docs/145: a hero in their own kit, breathing on the village's beat
                  : <HeroFigure heroId={h.id} look={h.look} classId={h.classId} frame={phase === i % 2 ? 1 : 0} scale={scale} className="fk-village__body fk-village__kit" />}</span>
              {talk.has(h.key) && <span key={talk.get(h.key)!.n} className="fk-village__say" data-testid="village-say">{talk.get(h.key)!.text}</span>}
              {innStage && (h.mine !== false
                ? <HeroBits heroKey={h.key} act={innActs[h.key]} mug={innMugs[h.key]} book={innBook?.key === h.key ? innBook.title : undefined} scale={scale} sheets={sheets} phase={phase} />
                // docs/120 VM2: what another player's hero is doing, from their row (the book they read is the one my view would give them)
                : <HeroBits heroKey={h.key} act={remoteActs.get(h.key)?.act} mug={remoteActs.get(h.key)?.mug} book={remoteActs.get(h.key)?.act === 'read' ? bookFor(look.seed, h.key) : undefined} scale={scale} sheets={sheets} phase={phase} />)}
              {h.id !== undefined && <EmoteAnchor heroes={[{ id: h.id, name: h.name, mine: !!h.mine }]} />}
            </span>
          );
        })}
        {/* the villagers, ambling about the lane when nobody is talking to them */}
        {here.map((n, i) => {
          const st = places.get(n.key);
          if (!st) return null;
          const k = `n:${n.key}`;
          const p = ensure(k, st);
          const isTalking = talking === n.key;
          return (
            <button key={n.key} ref={mover.reg(k)} type="button" className={`fk-hit fk-village__figure fk-village__npc${isTalking || walkingTo === n.key ? ' fk-village__npc--talking' : ''}${st.seated ? ' fk-village__npc--seated' : ''}`}
              data-testid="village-npc" data-npc={n.key} data-name={n.name} data-face={p.face < 0 ? 'left' : 'right'} aria-label={`${n.name}, ${n.epithet}${n.deal ? `, ${DEAL_WORD[n.deal]}${n.price ? ` (${n.price} gold)` : ''}` : ''}${n.quest ? ', has word of a quest' : ''}${n.alarm ? ', calling for help' : ''}`}
              style={{ ...at(p.x, p.y), zIndex: Math.round(p.y * 10) + 1 }}
              onClick={() => approach(n.key)}>
              <span className="fk-village__shadow" />
              <span className="fk-village__bob" style={{ animationDelay: `${-hashN(look.seed, i, 31) * 20}s` }}>
                <span className="fk-village__face"><Sprite sheet={sheets?.creatures} frame={n.frame + ((phase + i) % 2 ? 18 : 0)} scale={scale} className="fk-village__body" /></span>
              </span>
              {n.alarm ? <span className="fk-village__mark fk-village__mark--alarm" aria-hidden="true">!</span> : n.quest ? <span className="fk-village__mark fk-village__mark--quest" aria-hidden="true">!</span> : null}
              {n.deal && <span className={`fk-village__deal fk-village__deal--${n.deal}`} aria-hidden="true"><ItemIcon sheet={sheets?.items} n={DEAL_ICON[n.deal]} scale={1} /></span>}
              <span className="fk-village__plate fk-village__plate--npc"><span className="fk-village__plate-name">{n.name}</span><span className="fk-village__plate-sub">{n.epithet}{n.deal ? ` · ${DEAL_WORD[n.deal]}${n.price ? ` · ${n.price}g` : ''}` : ''}</span></span>
            </button>
          );
        })}
        {/* the serving hand with the tray (docs/64 VP4) */}
        {helper && room && (() => { const p = ensure('helper', { x: room.w - 2, y: room.floor }, -1); return (
          <span ref={mover.reg('helper')} className="fk-village__figure fk-village__helper" data-testid="village-helper" data-face="left" style={{ ...at(p.x, p.y), zIndex: Math.round(p.y * 10) + 1 }} aria-hidden="true">
            <span className="fk-village__shadow" />
            <span className="fk-village__face"><Sprite sheet={sheets?.creatures} frame={43 + (phase ? 18 : 0)} scale={scale} className="fk-village__body" /></span>
            {dishes.length === 0 && <span className="fk-village__tray"><Dish kind="stew" left={0} scale={scale} /><Dish kind="mug" left={0} scale={scale} /></span>}
          </span>
        ); })()}
        {/* docs/121 VW10: the serving hands bringing other bands their meals */}
        {remoteHands.map((h) => { const p = mover.at(h.key) ?? h.at; return (
          <span key={h.key} ref={mover.reg(h.key)} className="fk-village__figure fk-village__helper" data-testid="village-helper" data-remote="1" data-face={h.leg === 'out' ? 'left' : 'right'} style={{ ...at(p.x, p.y), zIndex: Math.round(p.y * 10) + 1 }} aria-hidden="true">
            <span className="fk-village__shadow" />
            <span className="fk-village__face"><Sprite sheet={sheets?.creatures} frame={43 + (phase ? 18 : 0)} scale={scale} className="fk-village__body" /></span>
            {h.tray && <span className="fk-village__tray"><Dish kind="stew" left={0} scale={scale} /><Dish kind="mug" left={0} scale={scale} /></span>}
          </span>
        ); })}
        {/* passers-by and the hens (docs/64 VP6): nobody to talk to, only life about the place */}
        {passers.map((w) => {
          const k = `w:${w.k}`;
          const p = mover.at(k);
          if (!p) return null;
          return (
            <span key={w.k} ref={mover.reg(k)} className={`fk-village__figure fk-village__passer${w.intoDoor ? ' fk-village__passer--door' : ''}`} data-testid="village-passer" data-k={k} data-kind={w.kind} data-face={p.face < 0 ? 'left' : 'right'} style={{ ...at(p.x, p.y), zIndex: Math.round(p.y * 10) + 1 }} aria-hidden="true">
              <span className="fk-village__shadow" />
              <span className="fk-village__face">{w.kind === 'folk' ? <Sprite sheet={sheets?.creatures} frame={w.frame + (phase ? 18 : 0)} scale={scale} className="fk-village__body" /> : <Critter kind={w.kind} coat={w.coat} frame={phase} scale={scale + 1} />}</span>
            </span>
          );
        })}
        {motion && hens.map((home, h) => {
          const k = `hen:${h}`;
          const p = ensure(k, home, h ? -1 : 1);
          return (
            <span key={k} ref={mover.reg(k)} className="fk-village__figure fk-village__passer" data-testid="village-passer" data-k={k} data-kind="hen" data-face={p.face < 0 ? 'left' : 'right'} style={{ ...at(p.x, p.y), zIndex: Math.round(p.y * 10) + 1 }} aria-hidden="true">
              <span className="fk-village__face"><Critter kind="hen" coat={h + Math.floor(look.seed % 2)} frame={phase} scale={scale + 1} /></span>
            </span>
          );
        })}
        {innStage && <InnLayer stage={innStage} />}
        <canvas ref={frontRef} className="fk-village__canvas fk-village__canvas--front" width={gw * CELL} height={gh * CELL} style={{ width: stageW, height: stageH }} aria-hidden="true" />
        {/* what is on the table: over the table top, which is over the diners' laps */}
        {[...dishes, ...remoteDishes].map((d) => <span key={d.key} className="fk-village__dish" data-testid="village-dish" data-left={d.left} style={at(d.x, d.y)} aria-hidden="true"><Dish kind={d.kind} left={d.left} scale={scale} /></span>)}
      </div>

      {/* the weather over everything (the intro's layers), the season's dressing, a vignette */}
      <div className="fk-village__weather" aria-hidden="true">
        {(weather === 'rain' || weather === 'storm') && <span className="fk-scene__rain" />}
        {weather === 'storm' && <span className="fk-scene__flash" />}
        {weather === 'fog' && <><span className="fk-scene__mist" /><span className="fk-scene__fog" /></>}
        {weather === 'snow' && <div className="fk-scene__snow">{flakes(70, 3, [3, 4], [7, 12]).map((st, i) => <span key={i} style={st} />)}</div>}
        {weather === 'heat' && <><span className="fk-scene__heat" /><span className="fk-scene__haze fk-scene__haze--heat" /></>}
        {weather === 'sandstorm' && <><span className="fk-scene__dust" /><span className="fk-scene__sand" /><span className="fk-scene__sand fk-scene__sand--far" /></>}
        {weather === 'blizzard' && <><span className="fk-scene__whiteout" /><div className="fk-scene__blizzard">{flakes(150, 3, [3, 5], [1.6, 3.2]).map((st, i) => <span key={i} style={st} />)}</div></>}
        {weather === 'frost' && <><span className="fk-scene__frost" /><div className="fk-scene__glints">{flakes(26, 9, [2, 3], [3, 6]).map((st, i) => <span key={i} style={{ ...st, top: `${(55 + hashN(i, 17) * 40).toFixed(1)}%` }} />)}</div></>}
        {weather === 'sleet' && <><span className="fk-scene__rain fk-scene__rain--sleet" /><div className="fk-scene__snow fk-scene__snow--sleet">{flakes(28, 5, [3, 3], [3.2, 5]).map((st, i) => <span key={i} style={st} />)}</div></>}
        {dressing === 'snow' && <div className="fk-scene__snow fk-scene__snow--light">{flakes(34, 11, [3, 4], [8, 12]).map((st, i) => <span key={i} style={st} />)}</div>}
        {dressing === 'leaves' && <div className="fk-scene__fall fk-scene__fall--leaves">{flakes(22, 13, [4, 5], [8, 13]).map((st, i) => <span key={i} style={st} />)}</div>}
        {dressing === 'petals' && <div className="fk-scene__fall fk-scene__fall--petals">{flakes(28, 17, [3, 4], [9, 14]).map((st, i) => <span key={i} style={st} />)}</div>}
        <span className={`fk-village__veil${inside ? ' fk-village__veil--room' : ''}`} />
      </div>
      {(children || innStage) && <div className="fk-village__overlays">{children}{innStage && inn && <InnOverlays data={inn} look={look} upstairs={spot === 'upstairs'} />}</div>}
    </div>
  );
}

